import SourceDocumentLinks from './SourceDocumentLinks';
import { flightSide, flightDayDifference, withFlightProgress, ON_TIME_TOLERANCE_MINUTES } from '../../utils/flightDisplay';
import FlightDataSummary from './FlightDataSummary';
import React, { useState, useEffect, useRef } from 'react';
import { getAirlineByCode, getAirlineByName, formatFlightNumber, extractFlightNumber, parseFlightNumber } from '../../utils/airlineUtils';
import { formatDateTimeWithTimezone } from '../../utils/dateTimeUtils';
import { Users, Car, ChevronDown, ChevronUp, RefreshCw, ArrowRight, X, Plane, Building, Utensils, Calendar, MapPin, StickyNote as StickyNoteIconLucide, Briefcase, AlertTriangle, Radio, Lock, LockOpen, FolderOpen, BadgeCheck, PlaneLanding, PlaneTakeoff, Mic } from 'lucide-react';
import StickyNote from './StickyNote';
import { FlightOperationalInfo, FlightRouteVisual } from './TimelineCardVisuals';
import FareTermsIcons, { resolveFareTerms } from './FareTermsIcons';
import FlightStatusBadge, { FlightLiveInfo, FlightGateInfo, FlightDelayIndicator } from './FlightStatusBadge';
import { displayStatus } from '../../utils/componentStatus';
import ParsingFeedbackModal from './ParsingFeedbackModal';
import { TripTimelineCard, tripCardKind } from './TripTimelineCards';
import ConfidentialPlaceholder from './ConfidentialPlaceholder';
import { ChauffeurStatusCompact, ChauffeurStatusBadge } from './ChauffeurStatusCard';
import { PassengerNameList, formatPassengerName } from './PassengerNameList';

// Component type configuration (shared ui-* tokens): gradients, colors, icons, and badges
const modernTypeConfig = {
  hotel: {
    gradient: 'from-ui-indigo-strong to-ui-violet-strong',
    border: 'border-ui-indigo-line',
    shadow: 'shadow-ui-indigo-solid/20',
    Icon: Building,
    badgeColor: 'bg-ui-indigo-bg text-ui-indigo-fg border-ui-indigo-line'
  },
  meeting: {
    gradient: 'from-ui-green-strong to-ui-teal-strong',
    border: 'border-ui-green-line',
    shadow: 'shadow-ui-green-solid/20',
    Icon: Users,
    badgeColor: 'bg-ui-green-bg text-ui-green-fg border-ui-green-line'
  },
  speaker_meeting: {
    gradient: 'from-ui-red-strong to-ui-pink-strong',
    border: 'border-ui-red-line',
    shadow: 'shadow-ui-red-solid/20',
    Icon: Mic,
    badgeColor: 'bg-ui-red-bg text-ui-red-fg border-ui-red-line'
  },
  restaurant: {
    gradient: 'from-ui-orange-strong to-ui-amber-strong',
    border: 'border-ui-orange-line',
    shadow: 'shadow-ui-orange-solid/20',
    Icon: Utensils,
    badgeColor: 'bg-ui-orange-bg text-ui-orange-fg border-ui-orange-line'
  },
  private_flight: {
    gradient: 'from-ui-violet-strong to-ui-indigo-strong',
    border: 'border-ui-violet-line',
    shadow: 'shadow-ui-violet-solid/20',
    Icon: Plane,
    badgeColor: 'bg-ui-violet-bg text-ui-violet-fg border-ui-violet-line'
  },
  commercial_flight: {
    gradient: 'from-ui-blue-strong to-ui-teal-strong',
    border: 'border-ui-blue-line',
    shadow: 'shadow-ui-blue-solid/20',
    Icon: Plane,
    badgeColor: 'bg-ui-blue-bg text-ui-blue-fg border-ui-blue-line'
  },
  flight: {
    gradient: 'from-ui-blue-strong to-ui-teal-strong',
    border: 'border-ui-blue-line',
    shadow: 'shadow-ui-blue-solid/20',
    Icon: Plane,
    badgeColor: 'bg-ui-blue-bg text-ui-blue-fg border-ui-blue-line'
  },
  rental_car: {
    gradient: 'from-ui-green-strong to-ui-green-strong',
    border: 'border-ui-green-line',
    shadow: 'shadow-ui-green-solid/20',
    Icon: Car,
    badgeColor: 'bg-ui-green-bg text-ui-green-fg border-ui-green-line'
  },
  car_rental: {
    gradient: 'from-ui-green-strong to-ui-green-strong',
    border: 'border-ui-green-line',
    shadow: 'shadow-ui-green-solid/20',
    Icon: Car,
    badgeColor: 'bg-ui-green-bg text-ui-green-fg border-ui-green-line'
  },
  chauffeur_service: {
    gradient: 'from-ui-teal-strong to-ui-teal-strong',
    border: 'border-ui-teal-line',
    shadow: 'shadow-ui-teal-solid/20',
    Icon: Car,
    badgeColor: 'bg-ui-teal-bg text-ui-teal-fg border-ui-teal-line'
  },
  airport_vip_service: {
    gradient: 'from-ui-amber-strong to-ui-amber-strong',
    border: 'border-ui-amber-line',
    shadow: 'shadow-ui-amber-solid/20',
    Icon: BadgeCheck,
    badgeColor: 'bg-ui-amber-bg text-ui-amber-fg border-ui-amber-line'
  },
  event: {
    gradient: 'from-ui-pink-strong to-ui-red-strong',
    border: 'border-ui-pink-line',
    shadow: 'shadow-ui-pink-solid/20',
    Icon: Calendar,
    badgeColor: 'bg-ui-pink-bg text-ui-pink-fg border-ui-pink-line'
  },
  memo: {
    gradient: 'from-ui-amber-strong to-ui-amber-strong',
    border: 'border-ui-amber-line',
    shadow: 'shadow-ui-amber-solid/20',
    Icon: StickyNoteIconLucide,
    badgeColor: 'bg-ui-amber-bg text-ui-amber-fg border-ui-amber-line',
    bgColor: 'bg-ui-amber-bg'
  },
  bundle: {
    gradient: 'from-ui-violet-strong to-ui-indigo-strong',
    border: 'border-ui-violet-line',
    shadow: 'shadow-ui-violet-solid/20',
    Icon: FolderOpen,
    badgeColor: 'bg-ui-violet-bg text-ui-violet-fg border-ui-violet-line'
  },
  general: {
    gradient: 'from-ui-muted to-ui-faint',
    border: 'border-ui-control',
    shadow: 'shadow-ui-ink/20',
    Icon: Calendar,
    badgeColor: 'bg-ui-hover text-ui-ink-2 border-ui-control'
  }
};

// Status pill colors (shared ui-* tokens)
const getModernStatusColor = (status) => {
  const s = String(status || 'pending').toLowerCase();
  if (s.includes('confirm')) return 'bg-ui-green-bg text-ui-green-fg border-ui-green-line';
  if (s.includes('request')) return 'bg-ui-amber-bg text-ui-amber-fg border-ui-amber-line';
  if (s.includes('cancel')) return 'bg-ui-red-bg text-ui-red-fg border-ui-red-line';
  if (s.includes('progress')) return 'bg-ui-amber-bg text-ui-amber-fg border-ui-amber-line';
  return 'bg-ui-hover text-ui-ink-2 border-ui-control';
};

/**
 * Component to display airline name with code lookup
 * Handles both cases:
 * 1. airlineValue is a 2-letter IATA code (e.g., "AA") - looks up the full name
 * 2. airlineValue is actually a full name (e.g., "Frontier Airlines") - looks up the code
 */
const AirlineDisplay = ({ airlineCode, showCode = false }) => {
  const [airlineName, setAirlineName] = useState(null);
  const [resolvedCode, setResolvedCode] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAirline = async () => {
      if (!airlineCode) {
        setLoading(false);
        return;
      }

      const input = String(airlineCode).trim();

      try {
        // Check if this looks like a 2-3 letter code or a full airline name
        const isLikelyCode = input.length <= 3 && /^[A-Z0-9]+$/i.test(input);

        if (isLikelyCode) {
          // It's a code - look up the full name
          const airline = await getAirlineByCode(input);
          if (airline) {
            setAirlineName(airline.name);
            setResolvedCode(input.toUpperCase());
          } else {
            // Code lookup failed, just use what we have
            setResolvedCode(input.toUpperCase());
          }
        } else {
          // It's a full name - look up the code via search API
          const airline = await getAirlineByName(input);
          if (airline) {
            setAirlineName(airline.name || input);
            setResolvedCode(airline.code ? airline.code.toUpperCase() : null);
          } else {
            // Name lookup failed, just use the name without code
            setAirlineName(input);
          }
        }
      } catch (error) {
        console.error('[AirlineDisplay] Error fetching airline:', error);
        // On error, try to use what we have
        const isLikelyCode = input.length <= 3 && /^[A-Z0-9]+$/i.test(input);
        if (isLikelyCode) {
          setResolvedCode(input.toUpperCase());
        } else {
          setAirlineName(input);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAirline();
  }, [airlineCode]);

  if (loading) {
    return <span>Loading...</span>;
  }

  if (!airlineCode) {
    return <span>—</span>;
  }

  // Display format: "Airline Name (CODE)" when showCode is true
  if (showCode && airlineName && resolvedCode) {
    return <span>{airlineName} ({resolvedCode})</span>;
  }

  // If we have only the name
  if (airlineName) {
    return <span>{airlineName}</span>;
  }

  // If we have only the code
  if (resolvedCode) {
    return <span>{resolvedCode}</span>;
  }

  // Fallback to just showing what was passed
  return <span>{airlineCode}</span>;
};

// Component to display formatted flight number
const FlightNumberDisplay = ({ flightNumber, airlineCode }) => {
  if (!flightNumber) {
    return <span>—</span>;
  }

  const formattedNumber = formatFlightNumber(flightNumber, airlineCode);
  return <span>{formattedNumber}</span>;
};

// Flight Passengers Display Component for Timeline 2.0 - handles individual legs
// Shows actual passenger names from parsed data, with executives displayed in bold
const FlightPassengersDisplay = ({ component, trip }) => {
  // Check if this is a flight leg (individual segment) or full flight component
  const isFlightLeg = component.isFlightLeg && component.flightLeg;

  let componentPassengers = [];

  // For grouped flight legs, use the merged passengers from all grouped components
  if (component.isGroupedFlightLeg && component.groupedPassengers?.length > 0) {
    componentPassengers = component.groupedPassengers;
  } else if (isFlightLeg) {
    if (component.isPrivateFlightLeg) {
      // For individual private flight legs, get passengers from the leg's passengers or the original component
      const leg = component.flightLeg;
      // Note: leg data stores passengers in 'leg_passengers' field (from TripComponentForm.js line 3725)
      const legPassengers = leg.leg_passengers || leg.passengers || [];
      // Check both 'passengers' and 'additional_travelers' for backward compatibility
      const originalPassengers = component.originalComponent?.component_data?.additional_travelers
        || component.originalComponent?.component_data?.passengers
        || [];

      // Use leg passengers if available, otherwise fall back to original component passengers
      componentPassengers = legPassengers.length > 0 ? legPassengers : originalPassengers;
    } else {
      // For individual commercial flight legs, first try to get passengers from the original component
      const originalPassengers = component.originalComponent?.component_data?.passengers || [];
      if (originalPassengers.length > 0) {
        componentPassengers = originalPassengers;
      } else {
        // Fall back to seat assignments if no passengers in original component
        const legSeatAssignments = component.flightLeg.seat_assignments || [];
        componentPassengers = legSeatAssignments.map(seat => ({
          name: seat.passenger_name,
          title: seat.passenger_title || '',
          executive_id: seat.executive_id || null
        }));
      }
    }
  } else {
    // For full flight components, get passengers from component_data
    // Check both 'passengers' and 'additional_travelers' for backward compatibility
    componentPassengers = component.component_data?.additional_travelers
      || component.component_data?.passengers
      || [];
  }

  // Get trip executives to check if a passenger is an executive
  const tripExecObjects = trip?.executives || [];
  const assignedExecutiveIds = Array.isArray(component.component_data?.assigned_executives)
    ? component.component_data.assigned_executives
    : [];

  // Process passengers - mark which ones are executives
  let allPassengers = componentPassengers.map(passenger => {
    const passengerName = passenger.name || '';
    // Check if this passenger has an executive_id (matched during parsing)
    const hasExecutiveId = !!passenger.executive_id;

    // Also check if passenger name matches any trip executive (for manually added components)
    let matchedExecutive = null;
    if (!hasExecutiveId && passengerName) {
      const cleanName = passengerName.toLowerCase().trim();
      matchedExecutive = tripExecObjects.find(exec => {
        const execFullName = `${exec.first_name || ''} ${exec.last_name || ''}`.toLowerCase().trim();
        return cleanName === execFullName ||
               cleanName.includes(exec.first_name?.toLowerCase()) && cleanName.includes(exec.last_name?.toLowerCase());
      });
    }

    return {
      ...passenger,
      isExecutive: hasExecutiveId || !!matchedExecutive
    };
  });

  // If we have no passengers at all, show a placeholder
  if (allPassengers.length === 0) {
    allPassengers.push({
      name: 'Passenger TBD',
      isExecutive: false
    });
  }

  // Sort passengers: executives first, then others
  const sortedPassengers = [...allPassengers].sort((a, b) => {
    if (a.isExecutive && !b.isExecutive) return -1;
    if (!a.isExecutive && b.isExecutive) return 1;
    return 0;
  });

  // One line always: as many names as fit, "+N" hover tooltip for the rest
  // (same behavior as the executive dashboard cards). Executives stay bold and
  // sort first so they remain in the visible portion.
  const entries = sortedPassengers.map(passenger => ({
    name: formatPassengerName(passenger.name) || passenger.name,
    emphasis: passenger.isExecutive
  }));

  return (
    <PassengerNameList
      names={entries}
      IconComponent={Users}
    />
  );
};

// Helper: Calculate day difference between two dates (for overnight flights)
const getDayDifference = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    // Compare dates only (ignore time)
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const diffTime = endDay.getTime() - startDay.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  } catch {
    return 0;
  }
};

// Helper: Build display props for the exec-style FlightRouteVisual from a
// flight-leg timeline item (commercial or private). Used by the "Cards" view
// mode. Field precedence mirrors the existing title/airline rendering above.
const buildFlightLegVisualProps = (component, prefs) => {
  const leg = component.flightLeg || {};
  const isPrivate = !!component.isPrivateFlightLeg;

  let dep, arr, chip = '', chipSub = '';

  if (isPrivate) {
    dep = {
      code: leg.departure_airport_iata || leg.iata_code || leg.departure_airport_code?.slice(-3) || leg.departure_airport_icao?.slice(-3) || '',
      name: leg.departure_airport_name || leg.departure_airport_display || leg.departure_airport || '',
      sub: leg.departure_fbo_name || '',
    };
    arr = {
      code: leg.arrival_airport_iata || leg.arrival_iata_code || leg.arrival_airport_code?.slice(-3) || leg.arrival_airport_icao?.slice(-3) || '',
      name: leg.arrival_airport_name || leg.arrival_airport_display || leg.arrival_airport || '',
      sub: leg.arrival_fbo_name || '',
    };
    chip = leg.tail_number || component.component_data?.tail_number || '';
  } else {
    dep = {
      code: leg.departure_airport || leg.departure_airport_code || '',
      name: leg.departure_airport_name || leg.departure_city || '',
    };
    arr = {
      code: leg.arrival_airport || leg.arrival_airport_code || '',
      name: leg.arrival_airport_name || leg.arrival_city || '',
    };
    let airlineCode = leg.airline_code || leg.iata_code || leg.airline || '';
    let flightNumber = leg.flight_number ? String(leg.flight_number) : '';
    const parsed = parseFlightNumber(flightNumber);
    // A usable chip is a short IATA/ICAO code; a full airline name is not
    if (!/^[A-Z0-9]{2,3}$/i.test(String(airlineCode))) {
      airlineCode = parsed.airlineCode || '';
    }
    if (parsed.airlineCode && parsed.flightNumber) {
      flightNumber = parsed.flightNumber;
    }
    chip = airlineCode ? String(airlineCode).toUpperCase() : '';
    chipSub = flightNumber;
  }

  const depDisplay = flightSide(leg, 'departure', prefs, component.start_datetime);
  const arrDisplay = flightSide(leg, 'arrival', prefs, component.end_datetime);
  return {
    dep: { ...dep, time: depDisplay.time, scheduledTime: depDisplay.kind !== 'Scheduled' ? depDisplay.scheduled.time : null },
    arr: { ...arr, time: arrDisplay.time, scheduledTime: arrDisplay.kind !== 'Scheduled' ? arrDisplay.scheduled.time : null },
    chip, chipSub, isPrivate,
    dayDiff: flightDayDifference(depDisplay, arrDisplay),
  };
};

// Helper: Format date/time range for flight components
const formatDateTimeRange = (component, prefs) => {
  // If this is a flight leg and has flightLeg object, use it exclusively
  if (component.isFlightLeg && component.flightLeg) {
    const leg = component.flightLeg;
    const depDisplay = flightSide(leg, 'departure', prefs, component.start_datetime);
    const arrDisplay = flightSide(leg, 'arrival', prefs, component.end_datetime);
    const dateStr = depDisplay.date || arrDisplay.date || 'Date TBD';
    const startTime = depDisplay.time;
    const endTime = arrDisplay.time;
    if (startTime && endTime) {
      return (
        <>
          <span>{dateStr}</span>
          <span className="ml-2 text-ui-muted">{startTime} - {endTime}</span>
        </>
      );
    }
    if (startTime) {
      return (
        <>
          <span>{dateStr}</span>
          <span className="ml-2 text-ui-muted">{startTime} - TBD</span>
        </>
      );
    }
    if (endTime) {
      return (
        <>
          <span>{dateStr}</span>
          <span className="ml-2 text-ui-muted">TBD - {endTime}</span>
        </>
      );
    }
    return dateStr;
  }
  
  // For non-flight-leg components, use standard datetime formatting
  if (component.start_datetime) {
    const startDate = new Date(component.start_datetime);
    const dateStr = fmtDate(startDate, prefs);
    const startTime = fmtTime(startDate, prefs);
    
    if (component.end_datetime) {
      const endDate = new Date(component.end_datetime);
      const endTime = fmtTime(endDate, prefs);
      return (
        <>
          <span>{dateStr}</span>
          <span className="ml-2 text-ui-muted">{startTime} - {endTime}</span>
        </>
      );
    }
    
    return (
      <>
        <span>{dateStr}</span>
        <span className="ml-2 text-ui-muted">{startTime}</span>
      </>
    );
  }
  
  return 'TBD';
};

// Helper: Format meeting line 2 (date/time) per spec
const formatMeetingDateLine = (component, prefs) => {
  const compData = component?.component_data || {};
  const start = component?.start_datetime;
  const end = component?.end_datetime;
  const meetingDate = compData.meeting_date; // YYYY-MM-DD
  const timeIsTBD = compData.time_is_tbd === true || component?.time_is_tbd === true;
  const dateIsTBD = component?.date_is_tbd === true;

  // Helper to format date with weekday + year per profile
  const formatDate = (dStr) => {
    try {
      const d = new Date(dStr);
      if (isNaN(d)) return 'TBD';
      return fmtWeekdayDate(d, prefs);
    } catch { return 'TBD'; }
  };

  // 1) If date is TBD, show full TBD
  if (dateIsTBD) {
    return 'TBD';
  }

  // 2) If time is TBD but date is set, show date with TBD time
  if (timeIsTBD) {
    // Use meeting_date if available, otherwise extract date from start_datetime
    let dateStr = '';
    if (meetingDate) {
      dateStr = formatDate(meetingDate);
    } else if (start) {
      dateStr = formatDate(start);
    } else {
      return 'TBD'; // No date info available
    }
    return `${dateStr} - TBD`;
  }

  // 3) start contains date but time TBD marker (legacy format support)
  if (typeof start === 'string' && start.includes('-TBD-TIME')) {
    const dOnly = start.split('-TBD-TIME')[0];
    return `${formatDate(dOnly)} - TBD`;
  }

  // 4) No date and no time → "TBD"
  if ((!start || start.trim() === '') && !meetingDate) {
    return 'TBD';
  }

  // 5) No start datetime, but we have meeting_date
  if ((!start || start.trim() === '') && meetingDate) {
    // No time field stored separately; just show date if present
    return formatDate(meetingDate);
  }

  // 4) We have real start/end datetimes → show date, and time range if we also have end
  if (start) {
    const dateStr = formatDate(start);
    // If time is TBD flag set but start exists (rare), still show TBD
    if (timeIsTBD) return `${dateStr} - TBD`;

    if (end) {
      const startTime = fmtTime(new Date(start), prefs);
      const endTime = fmtTime(new Date(end), prefs);
      return (
        <>
          {dateStr}
          <span className="ml-2 text-ui-muted">{startTime} - {endTime}</span>
        </>
      );
    } else {
      // If no end time, show date with start time only
      const startTime = fmtTime(new Date(start), prefs);
      return (
        <>
          {dateStr}
          <span className="ml-2 text-ui-muted">{startTime}</span>
        </>
      );
    }
  }

  return 'TBD';
};

// Helper function to determine if component is TBD (same logic as Timeline 2.0)
const isTBDComponent = (component) => {
  const compData = component?.component_data || {};
  const startDateTime = component?.start_datetime;
  const hasDateInData = !!(compData.meeting_date || compData.event_date || compData.restaurant_date);
  
  // SPECIAL CASE: Private flights without departure/arrival dates are TBD
  if (component?.component_type === 'private_flight') {
    // Check for details in multiple possible locations
    const details = compData.details || compData || {};
    const hasDepartureDate = details.departure_date && details.departure_date.trim() !== '';
    const hasArrivalDate = details.arrival_date && details.arrival_date.trim() !== '';
    const hasDepartureTime = details.departure_time && details.departure_time.trim() !== '' && details.departure_time.toLowerCase() !== 'tbd';
    const hasArrivalTime = details.arrival_time && details.arrival_time.trim() !== '' && details.arrival_time.toLowerCase() !== 'tbd';
    
    // If private flight has no dates/times set, it's TBD and draggable
    if (!hasDepartureDate && !hasArrivalDate && !hasDepartureTime && !hasArrivalTime) {
      return true;
    }
    
    // If private flight has partial date/time info but no start_datetime, it's likely TBD
    if (!startDateTime && (hasDepartureDate || hasArrivalDate || hasDepartureTime || hasArrivalTime)) {
      return true;
    }
  }
  
  // 1. If component has an actual start_datetime, it's NOT TBD (regardless of flag)
  if (startDateTime && typeof startDateTime === 'string' && startDateTime.trim() !== '') {
    // Exception: if start_datetime contains TBD-TIME format, it IS TBD
    if (startDateTime.includes('-TBD-TIME')) {
      return true;
    }
    // Otherwise, if it has a real datetime, it's fixed
    return false;
  }
  
  // 2. If no start_datetime but has date info in component_data, it's likely TBD
  if (!startDateTime && hasDateInData) {
    return true;
  }
  
  // 3. If component title contains "TBD", it's probably TBD
  if (component?.title && component.title.toLowerCase().includes('tbd')) {
    return true;
  }
  
  // 4. If time_is_tbd flag is true and no contradicting evidence, trust it
  if (compData.time_is_tbd === true && !startDateTime) {
    return true;
  }
  
  // 5. Default to NOT TBD for everything else
  return false;
};

import { useAuth } from '../../contexts/AuthContext';
import { formatDate as fmtDate, formatTime as fmtTime, formatWeekdayDate as fmtWeekdayDate, formatWeekdayDatePreserveTimezone, formatTimePreserveTimezone } from '../../utils/dateTimeUtils';

const TimelineComponentCard = ({
  component,
  formatDateTime,
  getComponentTypeName,
  onEdit,
  onDelete,
  onReassign, // New: callback for moving component to another trip
  allTrips = [], // New: list of all trips for reassign dropdown
  currentTripId, // New: current trip ID to exclude from dropdown
  viewMode = 'full', // Add viewMode prop with default
  trip, // Add trip prop for passenger information
  isHidden = false, // Hide component in summary view
  onToggleHide = () => {}, // Callback to toggle hide state
  // Sticky notes inside this component
  insideNotes = [],
  onToggleNoteExpand,
  onEditNote,
  onDeleteNote,
  onNotePositionChange, // Callback for floating note position change within same component
  onNoteMoveToComponent, // Callback to move note to a different component
  currentUserId,
  isConfidential = false, // Component confidentiality indicator
  canToggleConfidentiality = false, // Whether user can toggle confidentiality
  onToggleConfidentiality, // Callback to toggle confidentiality
  onOpenConfidentialityModal, // Callback to open confidentiality settings modal
  // Confidential placeholder reveal/conceal handling
  onRevealConfidential, // Callback to reveal a confidential placeholder
  onConcealConfidential, // Callback to conceal (hide) a revealed confidential component
  isRevealingId = null, // Component ID currently being revealed (for loading state)
  isConcealingId = null, // Component ID currently being concealed (for loading state)
  onQuickEdit, // Callback for Quick Edit on chauffeur_service components (opens UrbanrideQuickBooking in edit mode)
  onAddVipServiceForLeg, // Callback (componentId, legIndex, direction) to open Add Component pre-filled for an Airport VIP greet on a flight leg
  designedCard, // Full view: TripTimelineCard props (rides, stops, booking) to render this item as its designed card
  onInlineUpdateComponent // Callback for inline component data updates (e.g. anchor_to_arrival toggle)
}) => {
  // If this is a placeholder component, render the ConfidentialPlaceholder instead
  if (component.is_placeholder) {
    return (
      <ConfidentialPlaceholder
        component={component}
        onReveal={onRevealConfidential}
        onConceal={onConcealConfidential}
        isRevealing={isRevealingId === component.id}
        isConcealing={isConcealingId === component.id}
        isRevealed={false}
        showConcealOption={false}
      />
    );
  }

  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedPassengerIndex, setSelectedPassengerIndex] = useState(0); // Track selected passenger for details dropdown
  const [showReassign, setShowReassign] = useState(false); // Show reassign dropdown
  const [selectedTargetTrip, setSelectedTargetTrip] = useState(''); // Selected target trip for reassign
  const [showFeedbackModal, setShowFeedbackModal] = useState(false); // Parsing feedback modal
  const cardRef = useRef(null); // Ref for container bounds

  const { user } = useAuth();
  const prefs = user?.ui_preferences || { date_input_format: 'US', time_view_format: 'AMPM' };
  // Speaker portal accounts are strictly view-only: hide every mutating
  // affordance on the card regardless of what handlers the parent passed
  const readOnly = user?.role === 'speaker';

  // "Cards" view mode: exec-dashboard-style visuals inside the trip-page shell
  const isCardsMode = viewMode === 'cards';
  const showRouteVisual = isCardsMode && component.isFlightLeg && component.flightLeg;
  const operationalFlightLeg = withFlightProgress(component.flightLeg
    || component.component_data?.flight_legs?.[0]
    || null);
  const operationalFlightId = component.originalComponent?.component_id || component.component_id;
  const aircraftType = operationalFlightLeg?.aircraft_type
    || component.component_data?.aircraft_type
    || component.originalComponent?.component_data?.aircraft_type
    || '';
  const showOperationalInfo = Boolean(
    operationalFlightLeg
    && (component.component_type === 'commercial_flight'
      || component.component_type === 'flight'
      || component.component_type === 'private_flight')
  );

  // Details visibility: full view shows details by default, EXCEPT for
  // view-only speaker accounts, where details (confirmation, ticket, seat,
  // live flight info, bookings) start collapsed behind a chevron.
  const detailsVisible = (viewMode === 'full' && !readOnly) || isExpanded;

  // Speaker view: compact headlines to keep mobile cards tight —
  // flights show codes only ("LAS - LAX"), chauffeur rides show
  // "LAX to Balboa Bay Resort" (no "Urbanride:" prefix, no "Airport").
  const compactFlightTitle = (() => {
    if (!readOnly) return null;
    const cd = component.component_data || {};
    const leg = component.flightLeg;
    if (component.component_type === 'chauffeur_service') {
      const short = (type, name, address, airportCode) => {
        if (type === 'airport' && airportCode) return airportCode;
        let display = name || (address || '').split(',')[0] || '';
        // "LAX Airport" -> "LAX"
        display = display.replace(/^([A-Z]{3})\s+Airport\b/i, '$1');
        return display.trim();
      };
      const pickup = short(cd.pickup_type, cd.pickup_location, cd.pickup_address, cd.pickup_airport_code);
      const dropoff = short(cd.dropoff_type, cd.dropoff_location, cd.dropoff_address, cd.dropoff_airport_code);
      if (pickup && dropoff) return `${pickup} to ${dropoff}`;
      // Fallback: tidy the stored title ("Urbanride: LAX Airport to X" -> "LAX to X")
      if (component.title) {
        return component.title
          .replace(/^Urbanride:\s*/i, '')
          .replace(/\b([A-Z]{3})\s+Airport\b/g, '$1');
      }
      return null;
    }
    if (component.component_type === 'commercial_flight' || component.component_type === 'flight') {
      if (component.isFlightLeg && leg) {
        const dep = leg.departure_airport_iata || leg.departure_airport || leg.departure_airport_code || '';
        const arr = leg.arrival_airport_iata || leg.arrival_airport || leg.arrival_airport_code || '';
        if (dep && arr) return `${dep} - ${arr}`;
      } else {
        const legs = Array.isArray(cd.flight_legs) ? cd.flight_legs : [];
        if (legs.length > 0) {
          const first = legs[0];
          const last = legs[legs.length - 1];
          const dep = first.departure_airport_iata || first.departure_iata || first.departure_airport || first.departure_airport_code?.slice(-3) || '';
          const arr = last.arrival_airport_iata || last.arrival_iata || last.arrival_airport || last.arrival_airport_code?.slice(-3) || '';
          if (dep && arr) return `${dep} - ${arr}`;
        }
      }
    }
    if (component.component_type === 'private_flight') {
      if (component.isPrivateFlightLeg && leg) {
        const dep = leg.departure_airport_iata || leg.departure_airport_icao?.slice(-3) || leg.departure_airport || '';
        const arr = leg.arrival_airport_iata || leg.arrival_airport_icao?.slice(-3) || leg.arrival_airport || '';
        if (dep && arr) return `${dep} - ${arr}`;
      } else {
        const dep = cd.departure_airport_iata || cd.departure_airport_code?.slice(-3) || cd.departure_airport_icao?.slice(-3) || '';
        const arr = cd.arrival_airport_iata || cd.arrival_airport_code?.slice(-3) || cd.arrival_airport_icao?.slice(-3) || '';
        if (dep && arr) return `${dep} - ${arr}`;
      }
    }
    return null;
  })();

  // Speaker meetings label their reference as "Program ID" (matching the PDF);
  // parsed descriptions arrive as "Confirmation: NE-12345" so relabel on display.
  const displayDescription = component.component_type === 'speaker_meeting' && component.description
    ? component.description.replace(/^\s*Confirmation:/i, 'Program ID:')
    : component.description;

  const renderComponentDetails = () => {
    // In summary mode, never show details
    // In full mode, details show by default except for view-only speakers
    if (!detailsVisible) return null;

    return (
      <div className="mt-4 pt-4 border-t border-ui-divider">
        {/* Don't show description for commercial flights - confirmation info is in passenger details */}
        {displayDescription && component.component_type !== 'commercial_flight' && (
          <div className="mb-3">
            <div className="text-sm text-ui-ink-2">{displayDescription}</div>
          </div>
        )}
        {component.notes && (
          <div className="mb-3">
            <div className="text-xs font-medium mb-1 text-ui-ink-3">Notes:</div>
            <div className="text-sm text-ui-ink-2">{component.notes}</div>
          </div>
        )}

        {/* Chauffeur Service Stops (Full View) */}
        {component.component_type === 'chauffeur_service' && (
          (() => {
            const cd = component.component_data || {};
            const stops = cd.stops || [];

            // Only show this section if there are stops
            if (stops.length === 0) return null;

            // Helper function to format location display based on type
            const formatLocationDisplay = (type, name, address, airportCode, airlineCode, flightNumber) => {
              // As Directed: chauffeur service where passenger directs
              if (type === 'as_directed') {
                return 'As Directed';
              }
              // Airport: airport code + airline code + flight number
              if (type === 'airport' && airportCode) {
                const parts = [airportCode];
                if (airlineCode) parts.push(airlineCode);
                if (flightNumber) parts.push(flightNumber);
                return parts.join(' ');
              }
              // Hotel: hotel name, address
              if (type === 'hotel') {
                if (address && address !== name) {
                  return `${name}, ${address}`;
                }
                return address || name || 'Hotel';
              }
              // Address (including meeting): full address
              return address || name || 'Location TBD';
            };

            // Get pickup and dropoff display
            const pickupDisplay = formatLocationDisplay(
              cd.pickup_type,
              cd.pickup_location,
              cd.pickup_address,
              cd.pickup_airport_code,
              cd.pickup_airline_code,
              cd.pickup_flight_number
            );
            const dropoffDisplay = formatLocationDisplay(
              cd.dropoff_type,
              cd.dropoff_location,
              cd.dropoff_address,
              cd.dropoff_airport_code,
              cd.dropoff_airline_code,
              cd.dropoff_flight_number
            );

            // Helper function to format stop display
            const formatStopDisplay = (stop) => {
              // As Directed stop: chauffeur service where passenger directs
              if (stop.type === 'as_directed') {
                return 'As Directed';
              }
              // Airport stop: airport code + airline code + flight number
              if (stop.type === 'airport' && stop.airport_code) {
                const parts = [stop.airport_code];
                if (stop.airline_code) parts.push(stop.airline_code);
                if (stop.flight_number) parts.push(stop.flight_number);
                return parts.join(' ');
              }
              // Hotel stop: location name, address
              if (stop.type === 'hotel') {
                if (stop.location_address && stop.location_address !== stop.location) {
                  return `${stop.location}, ${stop.location_address}`;
                }
                return stop.location_address || stop.location || 'Hotel';
              }
              // Address (including meeting): use location_address for full address
              return stop.location_address || stop.location || 'Stop';
            };

            // Helper to ensure comment ends with a period
            const formatComment = (comment) => {
              if (!comment) return null;
              const trimmed = comment.trim();
              if (trimmed.endsWith('.') || trimmed.endsWith('!') || trimmed.endsWith('?')) {
                return trimmed;
              }
              return trimmed + '.';
            };

            return (
              <div className="mt-4 space-y-2">
                <div className="text-xs font-medium mb-2 text-ui-ink-3">Full Itinerary</div>
                <div className="space-y-2">
                  {/* Pickup */}
                  <div className="flex items-start gap-3 text-sm text-ui-ink-2">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-ui-green-strong text-ui-on-primary flex items-center justify-center text-[10px] font-medium">PU</div>
                    <div className="flex-1">
                      <div className="font-medium">{pickupDisplay}</div>
                    </div>
                  </div>

                  {/* Stops - indented relative to pickup/dropoff */}
                  {stops.map((stop, idx) => (
                    <div key={idx} className="flex items-start gap-3 text-sm ml-4 text-ui-ink-2">
                      <div className={`flex-shrink-0 w-6 h-6 rounded-full ${stop.type === 'as_directed' ? 'bg-ui-blue-strong' : 'bg-ui-red-strong'} text-ui-on-primary flex items-center justify-center text-[10px] font-medium`}>
                        {stop.type === 'as_directed' ? 'AD' : `S${idx + 1}`}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{formatStopDisplay(stop)}</div>
                        {stop.type === 'as_directed' ? (
                          <div className="text-xs text-ui-blue-fg">
                            Passenger/s will direct to one or more stops.
                          </div>
                        ) : stop.comment && (
                          <div className="text-xs text-ui-ink-3">
                            Chauffeur will wait {formatComment(stop.comment)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Dropoff */}
                  <div className="flex items-start gap-3 text-sm text-ui-ink-2">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full ${cd.dropoff_type === 'as_directed' ? 'bg-ui-blue-strong' : 'bg-ui-green-strong'} text-ui-on-primary flex items-center justify-center text-[10px] font-medium`}>
                      {cd.dropoff_type === 'as_directed' ? 'AD' : 'DO'}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{dropoffDisplay}</div>
                      {cd.dropoff_type === 'as_directed' && (
                        <div className="text-xs text-ui-blue-fg">
                          Passenger/s will direct to final destination.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()
        )}

        {/* Flight bottom rows (Full View) */}
        {(component.component_type === 'commercial_flight' || component.component_type === 'private_flight') && (
          <div className="mt-4 space-y-2">
            {/* Real-time flight updates for commercial flights */}
            {component.component_type === 'commercial_flight' && (() => {
              const leg = withFlightProgress(component.isFlightLeg && component.flightLeg
                ? component.flightLeg
                : (component.component_data?.flight_legs?.[0] || null));
              if (!leg) return null;

              // Check if there's any real-time data to show
              const hasEstimates = leg.estimated_departure || leg.estimated_arrival;
              const hasActuals = leg.actual_departure || leg.actual_arrival;
              const hasDelay = leg.delay_departure_minutes >= ON_TIME_TOLERANCE_MINUTES || leg.delay_arrival_minutes >= ON_TIME_TOLERANCE_MINUTES;
              const hasUpdate = leg.last_fa_update;
              const hasActiveAlert = leg.has_active_alert;

              // Show section if there's live data OR an active alert
              if (!hasEstimates && !hasActuals && !hasDelay && !hasUpdate && !hasActiveAlert) {
                return null;
              }

              return (
                <div className="rounded-lg p-3 border space-y-2 bg-ui-blue-bg border-ui-blue-line">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold uppercase tracking-wide text-ui-blue-fg">Live Flight Info</div>
                    {/* Active Alert Indicator */}
                    {hasActiveAlert && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-ui-green-bg text-ui-green-fg border border-ui-green-line"
                        title={`Flight alerts ${leg.alert_registration_status === 'registered' ? 'active' : 'pending'}`}
                      >
                        <Radio className="w-3 h-3" />
                        <span>Tracking</span>
                      </span>
                    )}
                  </div>

                  {/* Delay Indicators */}
                  {hasDelay && (
                    <div className="flex gap-4 text-sm">
                      {leg.delay_departure_minutes >= ON_TIME_TOLERANCE_MINUTES && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-ui-ink-3">Dep delay:</span>
                          <FlightDelayIndicator delayMinutes={leg.delay_departure_minutes} type="departure" variant="theme" />
                        </div>
                      )}
                      {leg.delay_arrival_minutes >= ON_TIME_TOLERANCE_MINUTES && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-ui-ink-3">Arr delay:</span>
                          <FlightDelayIndicator delayMinutes={leg.delay_arrival_minutes} type="arrival" variant="theme" />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Estimated/Actual Times - displayed in airport local timezone */}
                  {(hasEstimates || hasActuals) && (
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {(leg.estimated_departure || leg.actual_departure) && (
                        <div>
                          <span className="text-ui-ink-3">{leg.actual_departure ? 'Actual Dep:' : 'Est. Dep:'}</span>
                          <span className="ml-1 font-medium text-ui-ink">
                            {flightSide(leg, 'departure', prefs).time}
                          </span>
                        </div>
                      )}
                      {(leg.estimated_arrival || leg.actual_arrival) && (
                        <div>
                          <span className="text-ui-ink-3">{leg.actual_arrival ? 'Actual Arr:' : 'Est. Arr:'}</span>
                          <span className="ml-1 font-medium text-ui-ink">
                            {flightSide(leg, 'arrival', prefs).time}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Last Update Timestamp - shown in browser timezone with explicit indicator */}
                  {hasUpdate && (
                    <div className="text-[10px] text-ui-blue-fg">
                      Updated {formatDateTimeWithTimezone(leg.last_fa_update)}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* For private flights, include FBO addresses if available */}
            {component.component_type === 'private_flight' && (
              <>
                <div className="flex items-start gap-2 text-sm text-ui-ink-2">
                  <span className="w-44 shrink-0 text-xs text-ui-ink-3">Departure FBO</span>
                  <span className="font-medium">
                    {(() => {
                      const getFboInfo = (type) => {
                        if (component.isPrivateFlightLeg && component.flightLeg) {
                          // Individual private flight leg - get FBO info from leg
                          const leg = component.flightLeg;
                          return {
                            name: leg[`${type}_fbo_name`] || leg[`${type}_fbo`] || '',
                            address: leg[`${type}_fbo_address`] || leg[`${type}_fbo_full_address`] || '',
                            phone: leg[`${type}_fbo_phone`] || ''
                          };
                        } else {
                          // Full private flight component
                          const cd = component.component_data || {};
                          return {
                            name: cd[`${type}_fbo_name`] || cd[`${type}_fbo`] || '',
                            address: cd[`${type}_fbo_address`] || cd[`${type}_fbo_full_address`] || '',
                            phone: cd[`${type}_fbo_phone`] || ''
                          };
                        }
                      };
                      
                      const fbo = getFboInfo('departure');
                      
                      // Show FBO name even if address and phone are missing
                      if (!fbo.name) {
                        return '—';
                      }
                      
                      // Build display string with available information
                      let displayString = fbo.name;
                      
                      if (fbo.address) {
                        displayString += ` — ${fbo.address}`;
                      }
                      
                      if (fbo.phone) {
                        displayString += ` • ${fbo.phone}`;
                      }
                      
                      return displayString;
                    })()}
                  </span>
                </div>
                <div className="flex items-start gap-2 text-sm text-ui-ink-2">
                  <span className="w-44 shrink-0 text-xs text-ui-ink-3">Arrival FBO</span>
                  <span className="font-medium">
                    {(() => {
                      const getFboInfo = (type) => {
                        if (component.isPrivateFlightLeg && component.flightLeg) {
                          // Individual private flight leg - get FBO info from leg
                          const leg = component.flightLeg;
                          return {
                            name: leg[`${type}_fbo_name`] || leg[`${type}_fbo`] || '',
                            address: leg[`${type}_fbo_address`] || leg[`${type}_fbo_full_address`] || '',
                            phone: leg[`${type}_fbo_phone`] || ''
                          };
                        } else {
                          // Full private flight component
                          const cd = component.component_data || {};
                          return {
                            name: cd[`${type}_fbo_name`] || cd[`${type}_fbo`] || '',
                            address: cd[`${type}_fbo_address`] || cd[`${type}_fbo_full_address`] || '',
                            phone: cd[`${type}_fbo_phone`] || ''
                          };
                        }
                      };
                      
                      const fbo = getFboInfo('arrival');
                      
                      // Show FBO name even if address and phone are missing
                      if (!fbo.name) {
                        return '—';
                      }
                      
                      // Build display string with available information
                      let displayString = fbo.name;
                      
                      if (fbo.address) {
                        displayString += ` — ${fbo.address}`;
                      }
                      
                      if (fbo.phone) {
                        displayString += ` • ${fbo.phone}`;
                      }
                      
                      return displayString;
                    })()}
                  </span>
                </div>
              </>
            )}

            {/* Carrier (only for commercial flights) */}
            {component.component_type === 'commercial_flight' && (
            <div className="flex items-start gap-2 text-sm text-ui-ink-2">
              <span className="w-44 shrink-0 text-xs text-ui-ink-3">Carrier</span>
              <span className="font-medium">
                {(() => {
                  const cd = component.component_data || {};
                  let airlineCode = null;  // The 2-letter IATA code (e.g., "J2")
                  let airlineName = null;  // The full name (e.g., "Azerbaijan Airlines")

                  if (component.isFlightLeg && component.flightLeg) {
                    // For individual flight legs
                    const leg = component.flightLeg;
                    // Try to get the actual 2-letter code
                    airlineCode = leg.airline_code || leg.iata_code || leg.airline;
                    // Get the full name
                    airlineName = leg.airline_full_name || leg.airline_name;

                    // If airline_code looks like a name (more than 3 chars), swap them
                    if (airlineCode && airlineCode.length > 3) {
                      airlineName = airlineName || airlineCode;
                      airlineCode = null;
                    }

                    // Try to extract airline code from flight number if we don't have it
                    if (!airlineCode && leg.flight_number) {
                      const parsed = parseFlightNumber(leg.flight_number);
                      if (parsed.airlineCode) {
                        airlineCode = parsed.airlineCode;
                      }
                    }
                  } else if (cd.flight_legs && Array.isArray(cd.flight_legs)) {
                    // For full flight components, check first leg
                    const firstLeg = cd.flight_legs[0];
                    if (firstLeg) {
                      airlineCode = firstLeg.airline_code || firstLeg.iata_code || firstLeg.airline;
                      airlineName = firstLeg.airline_full_name || firstLeg.airline_name;

                      // If airline_code looks like a name, swap them
                      if (airlineCode && airlineCode.length > 3) {
                        airlineName = airlineName || airlineCode;
                        airlineCode = null;
                      }

                      // Try to extract from flight number
                      if (!airlineCode && firstLeg.flight_number) {
                        const parsed = parseFlightNumber(firstLeg.flight_number);
                        if (parsed.airlineCode) {
                          airlineCode = parsed.airlineCode;
                        }
                      }
                    }
                  }

                  // If we have both the full name and code, display directly
                  if (airlineName && airlineCode) {
                    return `${airlineName} (${airlineCode.toUpperCase()})`;
                  }

                  // If we only have the code, use AirlineDisplay to look up the name
                  if (airlineCode) {
                    return <AirlineDisplay airlineCode={airlineCode} showCode={true} />;
                  }

                  // If we only have the name, use AirlineDisplay to look up the code
                  if (airlineName) {
                    return <AirlineDisplay airlineCode={airlineName} showCode={true} />;
                  }

                  return '—';
                })()}
              </span>
            </div>
            )}

            {/* Flight number (or Tail number for private) */}
            <div className="flex items-start gap-2 text-sm text-ui-ink-2">
              <span className="w-44 shrink-0 text-xs text-ui-ink-3">{component.component_type === 'private_flight' ? 'Tail Number' : 'Flight number'}</span>
              <span className="font-medium">
                {(() => {
                  const cd = component.component_data || {};
                  if (component.component_type === 'private_flight') {
                    if (component.isPrivateFlightLeg && component.flightLeg) {
                      // Individual private flight leg - get tail number from leg or original component
                      const leg = component.flightLeg;
                      const tail = leg.tail_number || leg.tail || 
                                   component.originalComponent?.component_data?.tail_number || 
                                   component.originalComponent?.component_data?.tail ||
                                   cd.tail_number || cd.tail || cd.aircraft_tail || component.tail_number;
                      return tail || '—';
                    } else {
                      // Full private flight component
                      const tail = cd.tail_number || cd.tail || cd.aircraft_tail || component.tail_number;
                      return tail || '—';
                    }
                  }
                  // Gather flight numbers from legs first
                  const legs = Array.isArray(cd.flight_legs) ? cd.flight_legs : [];
                  let numbers = [];

                  if (component.isFlightLeg && component.flightLeg) {
                    // For individual flight legs, show only the numerical flight number
                    const leg = component.flightLeg;
                    const flightNumber = leg.flight_number;
                    const airlineCode = leg.airline_name || leg.airline;

                    // Extract just the number (remove airline code prefix if present)
                    const numericOnly = extractFlightNumber(flightNumber, airlineCode);
                    return numericOnly || '—';
                  } else if (legs.length > 0) {
                    // For multiple legs, extract just the numerical part of each flight number
                    const extractedNumbers = legs.map(l => {
                      const flightNumber = l?.flight_number;
                      const airlineCode = l?.airline_name || l?.airline;

                      if (flightNumber) {
                        return extractFlightNumber(flightNumber, airlineCode);
                      }
                      return null;
                    }).filter(Boolean);

                    return extractedNumbers.length ? extractedNumbers.join(', ') : '—';
                  } else if (cd.flight_number) {
                    numbers = [extractFlightNumber(cd.flight_number)];
                  } else if (component.flight_number) {
                    numbers = [extractFlightNumber(component.flight_number)];
                  }
                  return numbers.length ? Array.from(new Set(numbers)).join(', ') : '—';
                })()}
              </span>
            </div>

            {/* Passenger-specific details section for commercial flights */}
            {component.component_type === 'commercial_flight' && (
              (() => {
                const cd = component.component_data || {};
                const passengers = Array.isArray(cd.passengers) ? cd.passengers : [];

                // Get current flight leg info for seat assignment lookup
                const currentLeg = component.isFlightLeg && component.flightLeg ? component.flightLeg : null;
                const legIndex = component.legIndex;
                const legs = Array.isArray(cd.flight_legs) ? cd.flight_legs : [];

                // Helper to get passenger-specific seat for current leg
                const getPassengerSeat = (passenger, paxIndex) => {
                  // First check leg-specific seat assignments
                  if (currentLeg) {
                    const seatAssignments = currentLeg.seat_assignments || [];
                    // Find seat assignment matching this passenger
                    const seatAssignment = seatAssignments.find(sa =>
                      sa.passenger_name === passenger.name ||
                      sa.passenger_index === paxIndex
                    );
                    if (seatAssignment?.seat) return seatAssignment.seat;
                    // Also check leg's direct seat field (for single-passenger legs)
                    if (currentLeg.seat && passengers.length === 1) return currentLeg.seat;
                  }

                  // For full component view with multiple legs, show all seats for this passenger
                  if (!currentLeg && legs.length > 0) {
                    const passengerSeats = legs.map((leg, idx) => {
                      const seatAssignments = leg.seat_assignments || [];
                      const seatAssignment = seatAssignments.find(sa =>
                        sa.passenger_name === passenger.name ||
                        sa.passenger_index === paxIndex
                      );
                      if (seatAssignment?.seat) {
                        const route = leg.departure_airport && leg.arrival_airport
                          ? `${leg.departure_airport}→${leg.arrival_airport}`
                          : '';
                        return route ? `${seatAssignment.seat} (${route})` : seatAssignment.seat;
                      }
                      // Single passenger and leg has direct seat
                      if (leg.seat && passengers.length === 1) {
                        const route = leg.departure_airport && leg.arrival_airport
                          ? `${leg.departure_airport}→${leg.arrival_airport}`
                          : '';
                        return route ? `${leg.seat} (${route})` : leg.seat;
                      }
                      return null;
                    }).filter(Boolean);
                    return passengerSeats.length ? passengerSeats.join(', ') : null;
                  }

                  // Fallback to passenger-level seat
                  return passenger.seat || passenger.seat_number || null;
                };

                // If multiple passengers, show dropdown selector
                if (passengers.length > 1) {
                  const selectedPax = passengers[selectedPassengerIndex] || passengers[0];
                  const confirmation = selectedPax?.confirmation_number || cd.confirmation_number || component.confirmation_number || '—';
                  const ticket = selectedPax?.ticket_number || selectedPax?.ticket_no || selectedPax?.document_number || selectedPax?.e_ticket_number || cd.ticket_number || '—';
                  const seat = getPassengerSeat(selectedPax, selectedPassengerIndex) || '—';

                  // Check if this is a grouped component with multiple source components
                  const isGrouped = component.isGroupedFlightLeg && component.groupedComponents?.length > 1;

                  // Find the source component for the selected passenger (for delete)
                  const getSourceComponentForPassenger = (pax) => {
                    if (!isGrouped) return component.originalComponent || component;
                    const sourceId = pax._sourceComponentId;
                    return component.groupedComponents?.find(c => c.id === sourceId) || component.originalComponent || component;
                  };

                  return (
                    <>
                      {/* Passenger selector dropdown */}
                      <div className="flex items-start gap-2 text-sm text-ui-ink-2 mb-3">
                        <span className="w-44 shrink-0 text-xs text-ui-ink-3">Passenger Details</span>
                        <div className="flex items-center gap-2">
                          <select
                            value={selectedPassengerIndex}
                            onChange={(e) => setSelectedPassengerIndex(parseInt(e.target.value, 10))}
                            className="text-sm font-medium border rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-ui-blue-solid focus:border-ui-blue-solid bg-ui-sunk border-ui-control text-ui-ink"
                          >
                            {passengers.map((pax, idx) => (
                              <option key={idx} value={idx}>
                                {pax.name || `Passenger ${idx + 1}`}
                              </option>
                            ))}
                          </select>
                          {/* Edit button for selected passenger's booking (for grouped components) */}
                          {isGrouped && !readOnly && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const sourceComp = getSourceComponentForPassenger(selectedPax);
                                if (sourceComp) {
                                  onEdit(sourceComp);
                                }
                              }}
                              className="text-ui-muted hover:text-ui-ink p-1 rounded-md hover:bg-ui-hover"
                              title={`Edit ${selectedPax.name || 'passenger'}'s booking`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          {/* Delete button for selected passenger's booking (only for grouped components) */}
                          {isGrouped && !readOnly && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const sourceComp = getSourceComponentForPassenger(selectedPax);
                                if (sourceComp && window.confirm(`Delete ${selectedPax.name || 'this passenger'}'s booking?`)) {
                                  onDelete(sourceComp);
                                }
                              }}
                              className="text-ui-muted hover:text-ui-red-fg p-1 rounded-md hover:bg-ui-red-bg"
                              title={`Delete ${selectedPax.name || 'passenger'}'s booking`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Confirmation Number */}
                      <div className="flex items-start gap-2 text-sm text-ui-ink-2">
                        <span className="w-44 shrink-0 text-xs text-ui-ink-3">Confirmation Number</span>
                        <span className="font-medium">{confirmation}</span>
                      </div>

                      {/* Ticket Number */}
                      <div className="flex items-start gap-2 text-sm text-ui-ink-2">
                        <span className="w-44 shrink-0 text-xs text-ui-ink-3">Ticket Number</span>
                        <span className="font-medium">{ticket}</span>
                      </div>

                      {/* Seat Assignment */}
                      <div className="flex items-start gap-2 text-sm text-ui-ink-2">
                        <span className="w-44 shrink-0 text-xs text-ui-ink-3">Seat Assignment</span>
                        <span className="font-medium">{seat}</span>
                      </div>
                    </>
                  );
                }

                // Single passenger or no passengers - show details directly
                const singlePax = passengers[0] || {};
                const confirmation = singlePax.confirmation_number || cd.confirmation_number || component.confirmation_number || '—';
                const ticket = singlePax.ticket_number || singlePax.ticket_no || singlePax.document_number || singlePax.e_ticket_number || cd.ticket_number || '—';
                const seat = passengers.length === 1
                  ? (getPassengerSeat(singlePax, 0) || '—')
                  : (() => {
                      // No passengers - fallback to existing seat logic
                      if (currentLeg) {
                        return currentLeg.seat && currentLeg.seat.trim() ? currentLeg.seat : '—';
                      }
                      if (legs.length > 0) {
                        const legSeats = legs
                          .filter(leg => leg.seat && leg.seat.trim())
                          .map(leg => {
                            const departure = leg.departure_airport || leg.departure_airport_code || '';
                            const arrival = leg.arrival_airport || leg.arrival_airport_code || '';
                            const route = departure && arrival ? `${departure}→${arrival}` : '';
                            return route ? `${leg.seat} (${route})` : leg.seat;
                          });
                        return legSeats.length ? legSeats.join(', ') : '—';
                      }
                      return '—';
                    })();

                return (
                  <>
                    {/* Confirmation Number */}
                    <div className="flex items-start gap-2 text-sm text-ui-ink-2">
                      <span className="w-44 shrink-0 text-xs text-ui-ink-3">Confirmation Number</span>
                      <span className="font-medium">{confirmation}</span>
                    </div>

                    {/* Ticket Number */}
                    <div className="flex items-start gap-2 text-sm text-ui-ink-2">
                      <span className="w-44 shrink-0 text-xs text-ui-ink-3">Ticket Number</span>
                      <span className="font-medium">{ticket}</span>
                    </div>

                    {/* Seat Assignment */}
                    <div className="flex items-start gap-2 text-sm text-ui-ink-2">
                      <span className="w-44 shrink-0 text-xs text-ui-ink-3">Seat Assignment</span>
                      <span className="font-medium">{seat}</span>
                    </div>
                  </>
                );
              })()
            )}
          </div>
        )}
      </div>
    );
  };

  // Get modern theme config for this component type
  const modernConfig = modernTypeConfig[component.component_type] || modernTypeConfig.general;

  // Sections under the header: flight info, VIP greet, move-to-trip, full
  // details, Urbanride bookings and the hide toggle. Shared by both shells.
  const bodySections = (
    <>
      {/* Cards view mode: exec-dashboard-style route visual for flight legs */}
      {showRouteVisual && (
        <div className="mt-3 pt-3 border-t border-ui-divider">
          <FlightRouteVisual {...buildFlightLegVisualProps(component, prefs)} />
        </div>
      )}

      {showOperationalInfo && <FlightDataSummary leg={operationalFlightLeg} prefs={prefs}
        flightId={component.component_type === 'private_flight' || component.isPrivateFlightLeg ? null : operationalFlightId} />}
      {showOperationalInfo && (
        <FlightOperationalInfo
          departureTerminal={operationalFlightLeg.departure_terminal}
          departureGate={operationalFlightLeg.departure_gate}
          arrivalTerminal={operationalFlightLeg.arrival_terminal}
          arrivalGate={operationalFlightLeg.arrival_gate}
          departureCheckIn={operationalFlightLeg.departure_checkin}
          arrivalBaggageClaim={operationalFlightLeg.arrival_baggage_claim}
          aircraftType={aircraftType}
          showAirportDetails={component.component_type !== 'private_flight' && !component.isPrivateFlightLeg}
          trailing={<FareTermsIcons terms={resolveFareTerms(operationalFlightLeg,
            component.component_data || component.originalComponent?.component_data)} />}
        />
      )}

      {/* VIP Greet shortcut buttons — only for flight components */}
      {onAddVipServiceForLeg
        && (component.component_type === 'commercial_flight'
          || component.component_type === 'flight'
          || component.component_type === 'private_flight') && (() => {
        const sourceId = component.originalComponent?.id || component.id;
        const legIndex = (typeof component.legIndex === 'number') ? component.legIndex : 0;
        return (
          <div className="mt-3 pt-3 border-t flex flex-wrap gap-2 border-ui-divider">
            <span className="text-xs font-medium self-center mr-1 text-ui-ink-3">
              Add VIP Greet:
            </span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onAddVipServiceForLeg(sourceId, legIndex, 'departure'); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors bg-ui-amber-bg border-ui-amber-line text-ui-amber-fg hover:bg-ui-amber-line"
              title="Add an Airport VIP Greet for the departure of this segment"
            >
              <PlaneTakeoff className="w-3.5 h-3.5" />
              Departure
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onAddVipServiceForLeg(sourceId, legIndex, 'arrival'); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors bg-ui-amber-bg border-ui-amber-line text-ui-amber-fg hover:bg-ui-amber-line"
              title="Add an Airport VIP Greet for the arrival of this segment"
            >
              <PlaneLanding className="w-3.5 h-3.5" />
              Arrival
            </button>
          </div>
        );
      })()}

      {/* Reassign to Trip Section */}
      {showReassign && onReassign && (
        <div className="mt-3 pt-3 border-t border-ui-divider">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <select
                value={selectedTargetTrip}
                onChange={(e) => setSelectedTargetTrip(e.target.value)}
                className="w-full h-9 pl-3 pr-8 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-ui-blue-solid focus:border-ui-blue-solid appearance-none border-ui-control bg-ui-sunk text-ui-ink"
              >
                <option value="">Select trip...</option>
                {allTrips.filter(t => t.id !== currentTripId).map((trip) => (
                  <option key={trip.id} value={trip.id}>
                    {trip.title || 'Untitled Trip'}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-ui-ink-3" />
            </div>
            <button
              onClick={() => {
                if (selectedTargetTrip) {
                  // Get the original component ID for reassign
                  const compToReassign = (component.isFlightLeg || component.isPrivateFlightLeg || component.isHotelEvent || component.isRentalCarEvent) && component.originalComponent
                    ? component.originalComponent
                    : component;
                  onReassign(compToReassign, selectedTargetTrip);
                  setShowReassign(false);
                  setSelectedTargetTrip('');
                }
              }}
              disabled={!selectedTargetTrip}
              className="h-9 px-3 flex items-center gap-1.5 text-sm font-medium rounded-lg transition-colors text-ui-on-primary bg-ui-primary hover:bg-ui-primary-hover disabled:bg-ui-faint disabled:cursor-not-allowed"
            >
              <ArrowRight className="w-4 h-4" />
              Move
            </button>
            <button
              onClick={() => {
                setShowReassign(false);
                setSelectedTargetTrip('');
              }}
              className="h-9 px-2 rounded-lg transition-colors text-ui-ink-3 hover:text-ui-ink hover:bg-ui-hover"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Expandable Details */}
      {renderComponentDetails()}

      {/* Urbanride Bookings Section - Shows at bottom of card.
          For view-only speakers this counts as detail info: collapsed until expanded. */}
      {(!readOnly || detailsVisible) && (() => {
        const cd = component.component_data || {};
        const origCd = component.originalComponent?.component_data || {};

        // Helper function to format location display for itinerary summary
        const formatLocationSummary = (type, name, address, airportCode) => {
          if (type === 'airport' && airportCode) {
            return airportCode;
          }
          if (type === 'hotel') {
            return name || 'Hotel';
          }
          // For address/meeting, show a short version
          if (address) {
            // Take first part of address (before first comma)
            const shortAddress = address.split(',')[0];
            return shortAddress.length > 30 ? shortAddress.substring(0, 27) + '...' : shortAddress;
          }
          return name || 'Location';
        };

        // Helper to format stop for summary
        const formatStopSummary = (stop) => {
          if (stop.type === 'airport' && stop.airport_code) {
            return stop.airport_code;
          }
          if (stop.type === 'hotel') {
            return stop.location || 'Hotel';
          }
          const addr = stop.location_address || stop.location || 'Stop';
          const shortAddr = addr.split(',')[0];
          return shortAddr.length > 25 ? shortAddr.substring(0, 22) + '...' : shortAddr;
        };

        // Build itinerary summary with arrows
        const buildItinerarySummary = (bookingData) => {
          const pickup = formatLocationSummary(
            bookingData.pickup_type,
            bookingData.pickup_location,
            bookingData.pickup_address,
            bookingData.pickup_airport_code
          );
          const dropoff = formatLocationSummary(
            bookingData.dropoff_type,
            bookingData.dropoff_location,
            bookingData.dropoff_address,
            bookingData.dropoff_airport_code
          );
          const stops = bookingData.stops || [];

          if (stops.length === 0) {
            return `${pickup} → ${dropoff}`;
          } else if (stops.length === 1) {
            return `${pickup} → ${formatStopSummary(stops[0])} → ${dropoff}`;
          } else {
            // Multiple stops - show as pickup → ... → dropoff
            return `${pickup} → (${stops.length} stops) → ${dropoff}`;
          }
        };

        // Format passengers list with +X for extra guests
        const formatPassengers = (passengers) => {
          if (!passengers || passengers.length === 0) return 'Passengers TBD';
          return passengers.map(p => {
            const name = p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Guest';
            const extra = p.extra || 0;
            return extra > 0 ? `${name} +${extra}` : name;
          }).join(', ');
        };

        // Calculate total passenger count
        const getTotalPassengerCount = (passengers) => {
          if (!passengers || passengers.length === 0) return 0;
          return passengers.reduce((sum, p) => sum + 1 + (p.extra || 0), 0);
        };

        // Format date from pickup_datetime
        const formatPickupDate = (bookingData) => {
          if (!bookingData.pickup_datetime) return null;
          try {
            const dt = new Date(bookingData.pickup_datetime);
            if (!isNaN(dt.getTime())) {
              // Format as "Mon, Jun 26"
              return dt.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
            }
          } catch (e) {
            // Ignore date parsing errors
          }
          return null;
        };

        // Format pickup time and flight info (for airport pickups)
        const formatPickupInfo = (bookingData) => {
          // Extract time from pickup_datetime
          let timeStr = '';
          if (bookingData.pickup_datetime) {
            try {
              const dt = new Date(bookingData.pickup_datetime);
              if (!isNaN(dt.getTime())) {
                timeStr = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
              }
            } catch (e) {
              // Ignore date parsing errors
            }
          }

          if (!timeStr) return null;

          // For airport pickups, include full flight details: "14:25 JFK EK 201"
          if (bookingData.pickup_type === 'airport') {
            const parts = [timeStr];
            if (bookingData.pickup_airport_code) parts.push(bookingData.pickup_airport_code);
            if (bookingData.pickup_airline_code) parts.push(bookingData.pickup_airline_code);
            if (bookingData.pickup_flight_number) parts.push(bookingData.pickup_flight_number);
            return parts.join(' ');
          }

          // For non-airport pickups, just show time
          return timeStr;
        };

        // Render a single booking entry
        const renderBookingEntry = (bookingData, urcn, index = 0, isOnly = true) => {
          const pickupDate = formatPickupDate(bookingData);
          const pickupInfo = formatPickupInfo(bookingData);
          const vehicleClass = bookingData.vehicle_class || '';
          return (
            <div key={index} className={`${!isOnly && index > 0 ? `mt-3 pt-3 border-t border-ui-divider` : ''}`}>
              {/* URCN, Date, Pickup Time/Flight Info, and Itinerary on same line */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-ui-blue-bg text-ui-blue-fg border-ui-blue-line">
                  <Car className="w-3 h-3" />
                  {urcn}
                </span>
                {pickupDate && (
                  <span className="text-sm text-ui-ink-3">
                    {pickupDate}
                  </span>
                )}
                {pickupInfo && (
                  <span className="text-sm font-medium text-ui-ink-2">
                    {pickupInfo}
                  </span>
                )}
                <span className="text-sm text-ui-ink-3">
                  {buildItinerarySummary(bookingData)}
                </span>
              </div>
              {/* Passengers and vehicle type on next line */}
              <div className="flex items-center gap-1 mt-1 text-sm text-ui-ink-3">
                <Users className="w-3.5 h-3.5" />
                <span>{formatPassengers(bookingData.passengers)}</span>
                {(() => {
                  const totalCount = getTotalPassengerCount(bookingData.passengers);
                  return totalCount > 1 ? <span className="text-ui-ink-3">({totalCount} total)</span> : null;
                })()}
                {vehicleClass && (
                  <>
                    <span className="mx-1">|</span>
                    <Car className="w-3.5 h-3.5" />
                    <span>{vehicleClass}</span>
                  </>
                )}
              </div>
            </div>
          );
        };

        // Skip for chauffeur_service components - they already display all booking details
        if (component.component_type === 'chauffeur_service') {
          return null;
        }

        // For other components (flights, hotels, meetings, etc.), show linked Urbanride bookings
        const allBookings = cd.urbanride_bookings || origCd.urbanride_bookings || [];
        // For flight legs, filter to only show bookings that include this specific leg
        const bookings = component.isFlightLeg && component.legIndex !== undefined
          ? allBookings.filter(b => b.leg_indices && b.leg_indices.includes(component.legIndex))
          : allBookings;

        if (bookings.length > 0) {
          return (
            <div className="mt-4 pt-3 border-t border-ui-divider">
              <div className="text-xs font-medium mb-2 text-ui-ink-3">
                Urbanride Booking{bookings.length > 1 ? 's' : ''}
              </div>
              {bookings.map((booking, idx) => renderBookingEntry(booking, booking.urcn, idx, bookings.length === 1))}
            </div>
          );
        }

        // Legacy: single booking reference (just URCN, no itinerary/passenger data stored on component)
        const legacyUrcn = cd.urbanride_booking_urcn || origCd.urbanride_booking_urcn;
        if (legacyUrcn) {
          // For flight legs, check if this specific leg was booked
          if (component.isFlightLeg && component.legIndex !== undefined) {
            const bookedLegIndices = origCd.urbanride_booking_leg_indices || [];
            if (bookedLegIndices.length > 0 && !bookedLegIndices.includes(component.legIndex)) {
              return null; // This leg wasn't booked
            }
          }

          return (
            <div className="mt-4 pt-3 border-t border-ui-divider">
              <div className="text-xs font-medium mb-2 text-ui-ink-3">Urbanride Booking</div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-ui-blue-bg text-ui-blue-fg border-ui-blue-line">
                  <Car className="w-3 h-3" />
                  {legacyUrcn}
                </span>
                <span className="text-xs text-ui-muted">(See chauffeur service card for details)</span>
              </div>
            </div>
          );
        }

        return null;
      })()}

      {/* Hide checkbox - only shown in full mode; a planner tool, hidden for view-only speakers */}
      {viewMode === 'full' && !readOnly && (
        <div className="flex justify-end mt-2 pt-2 border-t border-ui-divider">
          <label className="flex items-center gap-2 cursor-pointer text-xs text-ui-ink-3 hover:text-ui-ink-2">
            <input
              type="checkbox"
              checked={isHidden}
              onChange={onToggleHide}
              className="w-3.5 h-3.5 rounded cursor-pointer border-ui-control text-ui-muted focus:ring-ui-control bg-ui-sunk"
            />
            <span>Hide in summary</span>
          </label>
        </div>
      )}

    </>
  );

  const feedbackModal = (
    <>
      {/* Parsing Feedback Modal */}
      {showFeedbackModal && console.log('[Parsing Feedback] TimelineComponentCard modal props:', {
        currentTripId,
        componentId: component.id,
        'originalComponent.id': component.originalComponent?.id,
        parsing_log_id: component.parsing_log_id,
        'originalComponent.parsing_log_id': component.originalComponent?.parsing_log_id,
        document_id: component.document_id,
        'originalComponent.document_id': component.originalComponent?.document_id,
        'attached_files[0].documentId': component.component_data?.attached_files?.[0]?.documentId,
        'originalComponent.attached_files[0].documentId': component.originalComponent?.component_data?.attached_files?.[0]?.documentId,
        hasOriginalComponent: !!component.originalComponent
      })}
      <ParsingFeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        component={component.originalComponent || component}
        componentType={component.component_type}
        tripId={currentTripId}
        parsingLogId={
          component.parsing_log_id ||
          component.originalComponent?.parsing_log_id ||
          component.component_data?.attached_files?.[0]?.parsingLogId ||
          component.originalComponent?.component_data?.attached_files?.[0]?.parsingLogId
        }
        documentId={
          component.document_id ||
          component.originalComponent?.document_id ||
          component.component_data?.attached_files?.[0]?.documentId ||
          component.originalComponent?.component_data?.attached_files?.[0]?.documentId
        }
      />    </>
  );

  // Floating sticky notes placed inside this component
  const floatingNotes = insideNotes && insideNotes.length > 0 && insideNotes.map(note => (
    <div
      key={note.id}
      className="absolute z-20 pointer-events-auto"
      style={{
        left: `${note.position?.offset_x || 5}%`,
        top: `${note.position?.offset_y || 5}%`,
      }}
    >
      <StickyNote
        note={note}
        onToggleExpand={onToggleNoteExpand}
        onEdit={onEditNote}
        onDelete={onDeleteNote}
        isDraggable={true}
        currentUserId={currentUserId}
        isFloating={true}
        containerRef={cardRef}
        onPositionChange={onNotePositionChange}
        onMoveToComponent={onNoteMoveToComponent}
        currentComponentId={component.id}
      />
    </div>
  ));

  // Full view with a designed card for this type: the designed card is the
  // shell, and this card's sections and planner actions sit inside it.
  if (designedCard && !readOnly && tripCardKind(component)) {
    const iconAction = 'w-9 h-9 inline-flex items-center justify-center rounded-lg text-ui-muted hover:bg-ui-hover';
    return (
      <div ref={cardRef} data-component-id={component.id} className="flex-1 min-w-0 relative">
        {floatingNotes}
        <TripTimelineCard
          {...designedCard}
          component={component}
          trip={trip}
          prefs={prefs}
          insideNotes={[]}
          onEdit={onEdit ? () => onEdit(component.originalComponent || component) : undefined}
          onDelete={onDelete}
          onQuickEdit={onQuickEdit ? () => onQuickEdit(component) : undefined}
          footerActions={(
            <>
              {onReassign && allTrips.length > 1 && (
                <button type="button" onClick={(e) => { e.stopPropagation(); setShowReassign(!showReassign); }}
                  className={`${iconAction} hover:text-ui-blue-fg`} aria-label="Move to another trip" title="Move to another trip">
                  <RefreshCw className="w-4 h-4" />
                </button>
              )}
              <button type="button" onClick={(e) => { e.stopPropagation(); setShowFeedbackModal(true); }}
                className={`${iconAction} hover:text-ui-amber-fg`} aria-label="Report parsing issue" title="Report parsing issue">
                <AlertTriangle className="w-4 h-4" />
              </button>
            </>
          )}
          details={<div className="[&>*:first-child]:mt-0 [&>*:first-child]:pt-0 [&>*:first-child]:border-t-0">{bodySections}</div>}
        />
        {feedbackModal}
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      data-component-id={component.id}
      className={`flex-1 rounded-xl sm:rounded-2xl p-3 sm:p-5 transition-all duration-300 group relative shadow-ui-card ${modernConfig.bgColor || 'bg-ui-surface'} backdrop-blur-xl border ${modernConfig.bgColor ? modernConfig.border : 'border-ui-line'} hover:${modernConfig.border} hover:shadow-2xl hover:${modernConfig.shadow}`}
    >
      {/* Floating sticky notes inside this component */}
      {floatingNotes}
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2 sm:mb-3">
        <div className="flex items-start gap-2.5 sm:gap-4 flex-1 min-w-0">
          {/* Icon */}
          <div
            className={`flex-shrink-0 flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br ${modernConfig.gradient} shadow-lg`}
          >
            <modernConfig.Icon className="w-5 h-5 sm:w-6 sm:h-6 text-ui-on-primary" />
          </div>

          <div className="flex-1 min-w-0">
            {/* Line 1: Title + Badges (title hidden for flight legs in cards
                mode - the route visual below carries the airports) */}
            <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
              {!showRouteVisual && <h3 className={`font-semibold leading-tight break-words ${
                readOnly
                  ? 'text-sm sm:text-base text-ui-ink'
                  : 'text-base sm:text-lg text-ui-ink'
              }`}>
                {compactFlightTitle ? (
                  compactFlightTitle
                ) : component.component_type === 'private_flight' ? (
                  component.isPrivateFlightLeg && component.flightLeg ? (
                    // Individual private flight leg - use the title set in Timeline2.js
                    component.title
                  ) : (
                    // Full private flight component
                    (() => {
                      const cd = component.component_data || {};
                      const depName = cd.departure_airport_name || cd.departure_airport || '';
                      const depCode = cd.departure_airport_iata || cd.departure_airport_code?.slice(-3) || cd.departure_airport_icao?.slice(-3) || '';
                      const arrName = cd.arrival_airport_name || cd.arrival_airport || '';
                      const arrCode = cd.arrival_airport_iata || cd.arrival_airport_code?.slice(-3) || cd.arrival_airport_icao?.slice(-3) || '';
                      
                      const depDisplay = depName && depCode ? `${depName} (${depCode})` : (depCode || depName || 'TBD');
                      const arrDisplay = arrName && arrCode ? `${arrName} (${arrCode})` : (arrCode || arrName || 'TBD');
                      
                      return arrDisplay !== 'TBD' ? `${depDisplay} - ${arrDisplay}` : (component.title || 'Private Flight');
                    })()
                  )
                ) : component.component_type === 'commercial_flight' ? (
                  component.isFlightLeg && component.flightLeg ? (
                    // Individual commercial flight leg - use the title set in Timeline2.js
                    component.title
                  ) : (
                    // Full commercial flight component - use existing title or try to format if data available
                    (() => {
                      const cd = component.component_data || {};
                      const legs = Array.isArray(cd.flight_legs) ? cd.flight_legs : [];
                      
                      if (legs.length > 0) {
                        // Multi-leg flight - show first to last airport with 3-letter codes
                        const firstLeg = legs[0];
                        const lastLeg = legs[legs.length - 1];
                        
                        const depName = firstLeg.departure_airport_name || firstLeg.departure_airport || 'TBD';
                        const depCode = firstLeg.departure_airport_iata || firstLeg.departure_iata || firstLeg.departure_airport_code?.slice(-3) || '';
                        const arrName = lastLeg.arrival_airport_name || lastLeg.arrival_airport || 'TBD';
                        const arrCode = lastLeg.arrival_airport_iata || lastLeg.arrival_iata || lastLeg.arrival_airport_code?.slice(-3) || '';
                        
                        const depDisplay = depCode ? `${depName} (${depCode})` : depName;
                        const arrDisplay = arrCode ? `${arrName} (${arrCode})` : arrName;
                        
                        return `${depDisplay} - ${arrDisplay}`;
                      } else {
                        return component.title || 'Commercial Flight';
                      }
                    })()
                  )
                ) : (
                  component.title
                )}
              </h3>}

              {/* Confidentiality Indicator */}
              {component.is_confidential && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-ui-amber-bg text-ui-amber-fg"
                  title="This component is confidential - only visible to specific users"
                >
                  <svg className="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </span>
              )}

              {/* Expand/Collapse Button - summary/cards modes, plus full mode
                  for view-only speakers (whose details start collapsed) */}
              {(viewMode === 'summary' || viewMode === 'cards' || (viewMode === 'full' && readOnly)) && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="flex-shrink-0 p-1 rounded-full transition-colors hover:bg-ui-hover"
                  title={isExpanded ? 'Show less' : 'Show more'}
                >
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-ui-ink-3" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-ui-ink-3" />
                  )}
                </button>
              )}

              {/* Trip Name Badge - redundant on the trip's own page for
                  view-only speakers, so hidden there */}
              {trip?.title && !readOnly && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-ui-hover text-ui-ink-2 border-ui-line">
                  {trip.title}
                </span>
              )}

              {/* Urbanride Booking Badge */}
              {(() => {
                const cd = component.component_data || {};
                const origCd = component.originalComponent?.component_data || {};

                // For chauffeur_service cards: show URCN badge and chauffeur status
                if (component.component_type === 'chauffeur_service') {
                  const urcn = component.confirmation_number || cd.urbanride_urcn || cd.confirmation_number;
                  return (
                    <>
                      {urcn && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-ui-blue-bg text-ui-blue-fg border-ui-blue-line" title={`Urbanride: ${urcn}`}>
                          <Car className="w-3 h-3" />
                          {urcn}
                        </span>
                      )}
                      <ChauffeurStatusBadge componentData={cd} />
                    </>
                  );
                }

                // For other components (flights, hotels, meetings, etc.):
                // Check for URCN from different sources:
                // 1. urbanride_bookings array - for components with multiple bookings (new format)
                // 2. urbanride_booking_urcn - legacy format

                // Check for multiple bookings first
                const allBookings = cd.urbanride_bookings || origCd.urbanride_bookings || [];
                // For flight legs, filter to only show bookings that include this specific leg
                const bookings = component.isFlightLeg && component.legIndex !== undefined
                  ? allBookings.filter(b => b.leg_indices && b.leg_indices.includes(component.legIndex))
                  : allBookings;
                if (bookings.length > 0) {
                  // Multiple bookings - show count badge that links to full display at bottom
                  return (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-ui-blue-bg text-ui-blue-fg border-ui-blue-line" title={`${bookings.length} Urbanride booking(s)`}>
                      <Car className="w-3 h-3" />
                      {bookings.length} booking{bookings.length > 1 ? 's' : ''}
                    </span>
                  );
                }

                // Single booking - legacy format
                const urcn = cd.urbanride_booking_urcn || origCd.urbanride_booking_urcn;

                if (!urcn) return null;

                // For flight legs, check if this specific leg was booked
                if (component.isFlightLeg && component.legIndex !== undefined) {
                  const bookedLegIndices = component.originalComponent?.component_data?.urbanride_booking_leg_indices || [];
                  if (bookedLegIndices.length > 0 && !bookedLegIndices.includes(component.legIndex)) {
                    return null; // This leg wasn't booked, don't show badge
                  }
                }

                return (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-ui-blue-bg text-ui-blue-fg border-ui-blue-line" title={`Urbanride: ${urcn}`}>
                    <Car className="w-3 h-3" />
                    {urcn}
                  </span>
                );
              })()}

              {/* Status Badge — internal workflow state, hidden for view-only speakers.
                  Booked flights, hotels and rental cars show it only when cancelled. */}
              {!readOnly && displayStatus(component) && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                getModernStatusColor(displayStatus(component))
              }`}>
                {displayStatus(component).replace('_', ' ')}
              </span>
              )}

              {/* Component Type Badge */}
              <span className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                modernConfig.badgeColor
              }`}>
                {getComponentTypeName(component.component_type)}
              </span>

              {/* Confidential Badge — a planner control, hidden for view-only speakers
                  (they never see confidential components anyway) */}
              {!readOnly && (isConfidential ? (
                <button
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border cursor-pointer bg-ui-amber-bg text-ui-amber-fg border-ui-amber-line hover:bg-ui-amber-line transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onOpenConfidentialityModal) {
                      onOpenConfidentialityModal(component.id);
                    } else if (canToggleConfidentiality && onToggleConfidentiality) {
                      onToggleConfidentiality(component.id);
                    }
                  }}
                  title="Click to manage confidentiality settings"
                >
                  <Lock className="w-3 h-3 mr-1" />
                  Confidential
                </button>
              ) : (
                <button
                  className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium border cursor-pointer bg-ui-hover text-ui-ink-3 border-ui-line hover:bg-ui-line transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onOpenConfidentialityModal) {
                      onOpenConfidentialityModal(component.id);
                    } else if (canToggleConfidentiality && onToggleConfidentiality) {
                      onToggleConfidentiality(component.id);
                    }
                  }}
                  title="Click to manage confidentiality settings"
                >
                  <LockOpen className="w-3 h-3" />
                </button>
              ))}
            </div>

            {/* Line 2: Date/Time (Private flights show FBOs on line 1 next to airports) */}
            <div className="flex flex-col gap-1 text-xs sm:text-sm mb-1">
              <div className="flex items-center gap-1 text-ui-ink-3">
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {component.component_type === 'meeting' || component.component_type === 'memo' || component.component_type === 'speaker_meeting' ? (
                  <span>{formatMeetingDateLine(component, prefs)}</span>
                ) : (
                  component.component_type === 'commercial_flight' || component.component_type === 'private_flight' ? (
                    component.component_type === 'private_flight' && component.isPrivateFlightLeg && component.flightLeg ? (
                      // Individual private flight leg - Line 2: departure date time - arrival date time
                      <span>
                        {(() => {
                          const start = component.start_datetime;
                          const end = component.end_datetime;
                          if (!start) return 'TBD';
                          const startD = new Date(start);
                          const datePart = fmtWeekdayDate(startD, prefs);
                          // Use timezone-preserving format for flight times
                          const timePart = formatTimePreserveTimezone(start, prefs) + (end ? ' - ' + formatTimePreserveTimezone(end, prefs) : '');
                          const dayDiff = getDayDifference(start, end);
                          return (
                            <>
                              {datePart} {timePart}
                              {dayDiff > 0 && <span className="ml-1 text-ui-amber-fg font-medium">+{dayDiff}</span>}
                              {dayDiff < 0 && <span className="ml-1 text-ui-amber-fg font-medium">{dayDiff}</span>}
                            </>
                          );
                        })()}
                      </span>
                    ) : (
                      // Regular flight (commercial or full private flight)
                      <span>
                        {component.start_datetime && (
                          <>
                            {fmtWeekdayDate(new Date(component.start_datetime), prefs)}
                            <span className="ml-2 text-ui-muted">
                              {/* Use timezone-preserving format for flight times */}
                              {formatTimePreserveTimezone(component.start_datetime, prefs)}
                              {component.end_datetime ? ' - ' + formatTimePreserveTimezone(component.end_datetime, prefs) : ''}
                              {/* Show day difference indicator for overnight/multi-day flights */}
                              {(() => {
                                const dayDiff = getDayDifference(component.start_datetime, component.end_datetime);
                                if (dayDiff > 0) return <span className="ml-1 text-ui-amber-fg font-medium">+{dayDiff}</span>;
                                if (dayDiff < 0) return <span className="ml-1 text-ui-amber-fg font-medium">{dayDiff}</span>;
                                return null;
                              })()}
                            </span>
                          </>
                        )}
                      </span>
                    )
                  ) : component.component_type === 'hotel' || component.isHotelEvent ? (
                    // Hotels: use timezone-preserving format to show time in hotel's local timezone
                    <span>
                      {component.start_datetime && (
                        <>
                          {fmtWeekdayDate(new Date(component.start_datetime), prefs)}
                          <span className="ml-2 text-ui-muted">
                            {formatTimePreserveTimezone(component.start_datetime, prefs)}
                          </span>
                        </>
                      )}
                    </span>
                  ) : component.component_type === 'rental_car' || component.component_type === 'car_rental' || component.isRentalCarEvent ? (
                    // Rental cars: use timezone-preserving format to show time in pickup/dropoff local timezone
                    <span>
                      {component.start_datetime && (
                        <>
                          {formatWeekdayDatePreserveTimezone(component.start_datetime, prefs)}
                          <span className="ml-2 text-ui-muted">
                            {formatTimePreserveTimezone(component.start_datetime, prefs)}
                            {component.end_datetime ? ' - ' + formatTimePreserveTimezone(component.end_datetime, prefs) : ''}
                          </span>
                        </>
                      )}
                    </span>
                  ) : (
                    (
                      <span>
                        {component.start_datetime && (
                          <>
                            {/* Use timezone-preserving format for chauffeur_service to avoid browser TZ conversion */}
                            {component.component_type === 'chauffeur_service'
                              ? formatWeekdayDatePreserveTimezone(component.start_datetime, prefs)
                              : fmtWeekdayDate(new Date(component.start_datetime), prefs)
                            }
                            <span className="ml-2 text-ui-muted">
                              {component.component_type === 'chauffeur_service'
                                ? formatTimePreserveTimezone(component.start_datetime, prefs)
                                : fmtTime(new Date(component.start_datetime), prefs)
                              }
                              {/* Don't show end time for chauffeur_service transfers */}
                              {component.component_type !== 'chauffeur_service' && component.end_datetime ? ' - ' + fmtTime(new Date(component.end_datetime), prefs) : ''}
                            </span>
                          </>
                        )}
                      </span>
                    )
                  )
                )}
                <SourceDocumentLinks component={component} />
              {/* Flight Status Badge and Tracking Indicator - for commercial flights with real-time updates */}
              {component.component_type === 'commercial_flight' && (() => {
                const leg = withFlightProgress(component.isFlightLeg && component.flightLeg
                  ? component.flightLeg
                  : (component.component_data?.flight_legs?.[0] || null));
                if (!leg) return null;
                const status = leg.flight_status;
                const delay = leg.delay_minutes;
                const hasActiveAlert = leg.has_active_alert;
                // Only show badges if there's a real status, updates, or active alert
                if (!status && delay == null && !leg.last_fa_update && !hasActiveAlert) return null;
                return (
                  <div className="flex items-center gap-1.5">
                    {hasActiveAlert && (
                      <span
                        className="inline-flex items-center p-1 rounded-full bg-ui-green-bg text-ui-green-fg"
                        title={`Flight alerts ${leg.alert_registration_status === 'registered' ? 'active' : 'pending'}`}
                      >
                        <Radio className="w-3 h-3" />
                      </span>
                    )}
                    {(status || delay != null || leg.last_fa_update) && (
                      <FlightStatusBadge
                        status={status || 'scheduled'}
                        delayMinutes={delay}
                        size="small"
                        variant="theme"
                      />
                    )}
                  </div>
                );
              })()}

              </div>

              {/* Anchor toggle for private flight legs */}
              {component.isPrivateFlightLeg && component.flightLeg && onInlineUpdateComponent && (() => {
                const leg = component.flightLeg;
                const hasDep = leg.departure_date && leg.departure_time &&
                  leg.departure_time.trim() !== '' && leg.departure_time.toLowerCase() !== 'tbd';
                const hasArr = leg.arrival_date && leg.arrival_time &&
                  leg.arrival_time.trim() !== '' && leg.arrival_time.toLowerCase() !== 'tbd';
                const isArrivalAnchored = leg.anchor_to_arrival || (!hasDep && hasArr);
                const isUserControlled = hasDep && hasArr;

                const handleToggleAnchor = (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isUserControlled) return;
                  const originalComp = component.originalComponent;
                  const updatedLegs = originalComp.component_data.private_flight_legs.map((l, idx) =>
                    idx === component.legIndex ? { ...l, anchor_to_arrival: !l.anchor_to_arrival } : l
                  );
                  onInlineUpdateComponent(originalComp, {
                    ...originalComp,
                    component_data: { ...originalComp.component_data, private_flight_legs: updatedLegs }
                  });
                };

                return (
                  <div className="flex items-center gap-1.5 mt-1 mb-1 text-ui-muted">
                    <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                    <span className="text-xs">Anchor:</span>
                    {isUserControlled ? (
                      <button
                        onClick={handleToggleAnchor}
                        className={`text-xs font-medium px-1.5 py-0.5 rounded transition-colors ${
                          isArrivalAnchored
                            ? 'bg-ui-violet-bg text-ui-violet-fg hover:bg-ui-violet-line'
                            : 'bg-ui-hover text-ui-ink-2 hover:bg-ui-line'
                        }`}
                        title={isArrivalAnchored ? 'Click to anchor to departure' : 'Click to anchor to arrival'}
                      >
                        {isArrivalAnchored ? 'Arrival' : 'Departure'}
                      </button>
                    ) : (
                      <span className="text-xs italic text-ui-muted">
                        {isArrivalAnchored ? 'Arrival (auto)' : 'Departure (auto)'}
                      </span>
                    )}
                  </div>
                );
              })()}

              {/* Line 3+: For meetings, add separate rows for Location and Attendees */}
              {component.component_type === 'meeting' && (
                <>
                  {/* Line 3: Location */}
                  <div className="w-full flex items-center gap-1 mb-2 text-ui-ink-3">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 616 0z" />
                    </svg>
                    <span>{component.location && component.location.trim() !== '' ? component.location : 'TBD'}</span>
                  </div>
                  {/* Line 4: Attendees (Primary Executive first) */}
                  <div className="mb-2">
                  {(() => {
                    const cd = component.component_data || {};
                    const primaryExecId = cd.primary_executive_id;
                    const attendees = cd.meeting_attendees || cd.attendees || [];
                    const attendeeNames = attendees.map(a => a?.name).filter(Boolean);
                    const entries = [];

                    // Map primary executive ID to name using trip executives
                    if (primaryExecId) {
                      const tripExecs = trip?.executives || [];
                      const primaryExec = tripExecs.find(exec => exec.id === primaryExecId);
                      if (primaryExec) {
                        const primaryName = primaryExec.full_name || `${primaryExec.first_name} ${primaryExec.last_name}`;
                        entries.push({ name: `${primaryName} (Primary)`, emphasis: true });
                      } else {
                        // Fallback: show ID if name lookup fails
                        entries.push({ name: `Executive (ID: ${primaryExecId.slice(0, 8)}...) (Primary)`, emphasis: true });
                      }
                    }

                    attendeeNames.forEach(name => entries.push({ name, emphasis: false }));
                    if (entries.length === 0) entries.push({ name: 'Attendees TBD', emphasis: false });
                    return <PassengerNameList names={entries} />;
                  })()}
                  </div>
                </>
              )}

              {/* Line 3+: For speaker meetings, show venue, speaker, sponsor/product, and program ref */}
              {component.component_type === 'speaker_meeting' && (() => {
                const cd = component.component_data || {};
                const venue = (component.location && component.location.trim() !== '')
                  ? component.location
                  : [cd.venue_name, cd.venue_address].filter(Boolean).join(', ');
                const speaker = cd.speaker_name;
                const sponsorProduct = [cd.sponsor, cd.product_name].filter(Boolean).join(' · ');
                const programRef = cd.program_id || cd.confirmation_number || component.confirmation_number;
                const isVirtual = cd.is_virtual;
                return (
                  <>
                    {/* Venue / virtual */}
                    <div className="w-full flex items-center gap-1 mb-2 text-ui-ink-3">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 616 0z" />
                      </svg>
                      <span>{isVirtual ? 'Virtual program' : (venue && venue.trim() !== '' ? venue : 'TBD')}</span>
                    </div>
                    {/* Speaker */}
                    {speaker && (
                      <div className="flex items-center gap-1 mb-2 text-ui-ink-3">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A4 4 0 019 15h6a4 4 0 013.879 2.804M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>Speaker: {speaker}</span>
                      </div>
                    )}
                    {/* Sponsor · Product */}
                    {sponsorProduct && (
                      <div className="flex items-center gap-1 mb-2 text-ui-ink-3">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3m4-12h2m-2 4h2m6-4h2m-2 4h2" />
                        </svg>
                        <span>{sponsorProduct}</span>
                      </div>
                    )}
                    {/* Program ID / reference */}
                    {programRef && (
                      <div className="flex items-center gap-1 mb-2 text-ui-ink-3">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>Program: <span className="font-mono font-medium">{programRef}</span></span>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Non-meeting lines */}
              {component.component_type !== 'meeting' && component.component_type !== 'speaker_meeting' && (
                component.component_type === 'hotel' ? (
                  <>
                    {/* Line 3 for hotel: Address/Location */}
                    {component.location && component.location.trim() !== '' && component.location !== 'Location TBD' && (
                      <div className="w-full flex items-center gap-1 mb-2 text-ui-ink-3">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{component.location}</span>
                      </div>
                    )}
                    {/* Confirmation reference for hotel */}
                    {(() => {
                      const cd = component.component_data || {};
                      const confNum = cd.confirmation_number || component.confirmation_number;
                      if (!confNum) return null;
                      return (
                        <div className="flex items-center gap-1 mb-2 text-ui-ink-3">
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span>Ref: <span className="font-mono font-medium">{confNum}</span></span>
                        </div>
                      );
                    })()}
                    {/* Line 4 for hotel: Assigned executive(s) with per-room details when available */}
                    {(() => {
                      const cd = component.component_data || {};
                      const roomDetails = Array.isArray(cd.room_details) ? cd.room_details : [];
                      const tExecs = Array.isArray(trip?.executives) ? trip.executives : [];
                      const execById = new Map(tExecs.map(e => [String(e.id), `${e.first_name} ${e.last_name}`]));

                      // Helper to format room type for display
                      const formatRoomType = (roomType) => {
                        if (!roomType) return '';
                        const typeMap = {
                          'standard': 'Standard',
                          'deluxe': 'Deluxe',
                          'suite': 'Suite',
                          'junior_suite': 'Junior Suite',
                          'executive_suite': 'Executive Suite',
                          'presidential_suite': 'Presidential Suite',
                          'king': 'King',
                          'queen': 'Queen',
                          'double': 'Double',
                          'twin': 'Twin',
                          'connecting': 'Connecting',
                          'accessible': 'Accessible'
                        };
                        return typeMap[roomType] || roomType;
                      };

                      // Check if we have per-room guest assignments
                      const hasPerRoomGuests = roomDetails.some(room => (room.guest_ids || []).length > 0);

                      // Build a set of executive IDs for quick lookup
                      const executiveIds = new Set(tExecs.map(e => String(e.id)));

                      if (hasPerRoomGuests) {
                        // Collect all guests with their room types, marking executives
                        const allGuests = [];
                        roomDetails.forEach((room) => {
                          const guestIds = room.guest_ids || [];
                          const guestNames = room.guest_names || [];
                          const roomType = formatRoomType(room.room_type);

                          guestIds.forEach((id, i) => {
                            const name = guestNames[i] || execById.get(String(id)) || 'Guest';
                            const isExecutive = executiveIds.has(String(id));
                            allGuests.push({ name, roomType, isExecutive });
                          });
                        });

                        if (allGuests.length === 0) return null;

                        return (
                          <div className="mb-2">
                            <PassengerNameList
                              names={allGuests.map(guest => ({
                                name: guest.roomType ? `${guest.name} (${guest.roomType})` : guest.name,
                                emphasis: guest.isExecutive
                              }))}
                            />
                          </div>
                        );
                      }

                      // Fallback: show all assigned executives without room details
                      const assigned = Array.isArray(cd.assigned_executives) ? cd.assigned_executives : [];
                      let names = Array.isArray(cd.executive_names) ? cd.executive_names : [];
                      if ((!names || names.length === 0) && assigned.length > 0) {
                        names = assigned.map(id => execById.get(String(id))).filter(Boolean);
                      }
                      if ((!names || names.length === 0) && assigned.length > 0) {
                        names = [`${assigned.length} guest(s)`];
                      }
                      // speaker_bureau mode: single free-text primary guest
                      // (optionally linked to a Speaker), no executive roster.
                      if (!names || names.length === 0) {
                        const primaryGuest = cd.primary_guest_speaker_name || cd.primary_guest_name;
                        if (primaryGuest) names = [primaryGuest];
                      }
                      if (!names || names.length === 0) return null;
                      return (
                        <div className="mb-2">
                          <PassengerNameList names={names} />
                        </div>
                      );
                    })()}
                  </>
                ) : (component.component_type === 'commercial_flight' || component.component_type === 'private_flight' || component.component_type === 'flight') ? (
                  <div className="mb-2">
                    <FlightPassengersDisplay component={component} trip={trip} />
                  </div>
                ) : component.component_type === 'restaurant' ? (
                  <>
                    {/* Line 3: Restaurant details with full name, address, and phone on one line */}
                    <div className="flex items-center gap-1 text-ui-ink-3">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 616 0z" />
                      </svg>
                      <span>
                        {(() => {
                          const cd = component.component_data || {};
                          const restaurantName = cd.restaurant_name || component.title || component.location || 'Restaurant TBD';
                          const address = cd.address || '';
                          const phone = cd.phone || '';
                          
                          let displayText = restaurantName;
                          
                          if (address) {
                            displayText += ` • ${address}`;
                          }
                          
                          if (phone) {
                            displayText += ` • ${phone}`;
                          }
                          
                          if (!address && !phone) {
                            displayText += ' • Address & phone TBD';
                          }
                          
                          return displayText;
                        })()}
                      </span>
                    </div>
                    {/* Line 4: Reservation holder and attendees */}
                    <div>
                      {(() => {
                        const cd = component.component_data || {};
                        const reservationHolder = cd.reservation_holder_name || '';
                        const attendees = cd.restaurant_attendees || [];
                        const attendeeNames = attendees.map(a => a?.name).filter(Boolean);
                        const entries = [];

                        if (reservationHolder) {
                          entries.push({ name: `${reservationHolder} (Reservation Holder)`, emphasis: true });
                        }

                        attendeeNames.forEach(name => entries.push({ name, emphasis: false }));
                        if (entries.length === 0) entries.push({ name: 'Guests TBD', emphasis: false });
                        return <PassengerNameList names={entries} />;
                      })()}
                    </div>
                  </>
                ) : component.component_type === 'chauffeur_service' ? (
                  <>
                    {/* Line 3: Pickup -> Stops -> Dropoff for chauffeur service */}
                    <div className="flex items-center gap-1 mb-2 text-ui-ink-3">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="flex items-center flex-wrap">
                        {(() => {
                          const cd = component.component_data || {};
                          const stops = cd.stops || [];

                          // Helper function to format location display based on type
                          const formatLocationDisplay = (type, name, address, airportCode, airlineCode, flightNumber) => {
                            // Airport: airport code + airline code + flight number
                            if (type === 'airport' && airportCode) {
                              const parts = [airportCode];
                              if (airlineCode) parts.push(airlineCode);
                              if (flightNumber) parts.push(flightNumber);
                              return parts.join(' ');
                            }
                            // Hotel: hotel name, address
                            if (type === 'hotel') {
                              if (address && address !== name) {
                                return `${name}, ${address}`;
                              }
                              return address || name || 'Hotel';
                            }
                            // Address (including meeting): full address
                            return address || name || 'Location TBD';
                          };

                          // Helper function to format stop display
                          const formatStopDisplay = (stop) => {
                            // Airport stop: airport code + airline code + flight number
                            if (stop.type === 'airport' && stop.airport_code) {
                              const parts = [stop.airport_code];
                              if (stop.airline_code) parts.push(stop.airline_code);
                              if (stop.flight_number) parts.push(stop.flight_number);
                              return parts.join(' ');
                            }
                            // Hotel stop: location name, address
                            if (stop.type === 'hotel') {
                              if (stop.location_address && stop.location_address !== stop.location) {
                                return `${stop.location}, ${stop.location_address}`;
                              }
                              return stop.location_address || stop.location || 'Hotel';
                            }
                            // Address (including meeting): use location_address for full address
                            return stop.location_address || stop.location || 'Stop';
                          };

                          // Get pickup and dropoff display
                          const pickupDisplay = formatLocationDisplay(
                            cd.pickup_type,
                            cd.pickup_location,
                            cd.pickup_address,
                            cd.pickup_airport_code,
                            cd.pickup_airline_code,
                            cd.pickup_flight_number
                          );
                          const dropoffDisplay = formatLocationDisplay(
                            cd.dropoff_type,
                            cd.dropoff_location,
                            cd.dropoff_address,
                            cd.dropoff_airport_code,
                            cd.dropoff_airline_code,
                            cd.dropoff_flight_number
                          );

                          // If no stops, show simple pickup -> dropoff
                          if (stops.length === 0) {
                            return `${pickupDisplay} → ${dropoffDisplay}`;
                          }

                          // If single stop, show pickup -> stop -> dropoff
                          if (stops.length === 1) {
                            const stopLocation = formatStopDisplay(stops[0]);
                            return `${pickupDisplay} → ${stopLocation} → ${dropoffDisplay}`;
                          }

                          // If multiple stops, show pickup -> ... -> dropoff with hover tooltip
                          return (
                            <>
                              {pickupDisplay}
                              <span className="mx-1">→</span>
                              <span className="relative group/stops">
                                <span className="cursor-pointer text-ui-link hover:text-ui-blue-fg font-medium">...</span>
                                {/* Tooltip showing all stops */}
                                <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover/stops:block bg-ui-raised text-ui-ink border border-ui-line text-xs rounded-lg shadow-ui-pop p-3 min-w-max max-w-xs">
                                  <div className="font-semibold mb-2 text-ui-ink">Full Itinerary</div>
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                      <span className="w-4 h-4 rounded-full bg-ui-green-strong text-ui-on-primary flex items-center justify-center text-[10px]">1</span>
                                      <span>{pickupDisplay}</span>
                                      <span className="text-ui-ink-3 text-[10px]">(Pickup)</span>
                                    </div>
                                    {stops.map((stop, idx) => (
                                      <div key={idx} className="flex items-center gap-2">
                                        <span className="w-4 h-4 rounded-full bg-ui-blue-strong text-ui-on-primary flex items-center justify-center text-[10px]">{idx + 2}</span>
                                        <span>{formatStopDisplay(stop)}</span>
                                        {stop.comment && (
                                          <span className="text-ui-ink-3 text-[10px]">({stop.comment})</span>
                                        )}
                                      </div>
                                    ))}
                                    <div className="flex items-center gap-2">
                                      <span className="w-4 h-4 rounded-full bg-ui-red-strong text-ui-on-primary flex items-center justify-center text-[10px]">{stops.length + 2}</span>
                                      <span>{dropoffDisplay}</span>
                                      <span className="text-ui-ink-3 text-[10px]">(Dropoff)</span>
                                    </div>
                                  </div>
                                </div>
                              </span>
                              <span className="mx-1">→</span>
                              {dropoffDisplay}
                            </>
                          );
                        })()}
                      </span>
                    </div>
                    {/* Line 4: Passengers for chauffeur service */}
                    <div>
                      {(() => {
                        const cd = component.component_data || {};
                        const passengers = cd.passengers || [];
                        let names;
                        if (passengers.length > 0) {
                          // Format each passenger with +X for extra guests
                          names = passengers.map(p => {
                            const name = p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Guest';
                            const extra = p.extra || 0;
                            return extra > 0 ? `${name} +${extra}` : name;
                          });
                        } else if (cd.passenger_name) {
                          // Fallback to legacy single passenger
                          names = [cd.passenger_name];
                        } else {
                          names = ['Passenger TBD'];
                        }
                        return <PassengerNameList names={names} />;
                      })()}
                    </div>
                    {/* Chauffeur status and vehicle tracking */}
                    {(component.component_data?.chauffeur_status || component.component_data?.vehicle_status) && (
                      <ChauffeurStatusCompact component={component} />
                    )}
                    {/* Quick Edit link — always visible when feature is available */}
                    {(component.component_data?.urbanride_urcn || component.confirmation_number) && onQuickEdit && !readOnly && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onQuickEdit(component);
                        }}
                        className="mt-2 flex items-center gap-1 text-xs font-medium transition-colors text-ui-link hover:text-ui-blue-fg"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Quick Edit
                      </button>
                    )}
                  </>
                ) : component.component_type === 'airport_vip_service' ? (
                  (() => {
                    const cd = component.component_data || {};
                    const dirIsArrival = cd.service_direction === 'arrival';
                    const dirIsDeparture = cd.service_direction === 'departure';
                    const DirIcon = dirIsArrival ? PlaneLanding : dirIsDeparture ? PlaneTakeoff : Plane;
                    const dirLabel = dirIsArrival ? 'Arrival' : dirIsDeparture ? 'Departure' : '';
                    const apt = cd.airport_code || '';
                    const terminal = cd.terminal ? ` · ${cd.terminal}` : '';
                    const segmentLabel = cd.linked_segment_label || '';
                    const provider = cd.provider_name || '';
                    const greeter = [cd.greeter_name, cd.greeter_phone].filter(Boolean).join(' · ');
                    const meetLine = cd.greet_location || '';
                    const sign = cd.name_board_sign || '';
                    return (
                      <>
                        {/* Line 3: airport + direction + linked segment */}
                        <div className="flex items-center gap-1 mb-1 text-ui-ink-3">
                          <DirIcon className="w-4 h-4 flex-shrink-0" />
                          <span>
                            {[apt + terminal, dirLabel, segmentLabel].filter(Boolean).join(' · ') || 'Linked flight TBD'}
                          </span>
                        </div>
                        {/* Line 4: provider + greeter */}
                        {(provider || greeter) && (
                          <div className="flex items-center gap-1 mb-1 text-ui-ink-3">
                            <BadgeCheck className="w-4 h-4 flex-shrink-0" />
                            <span>
                              {provider}
                              {provider && greeter ? ' · ' : ''}
                              {greeter}
                            </span>
                          </div>
                        )}
                        {/* Line 5: meet location + name board */}
                        {(meetLine || sign) && (
                          <div className="flex items-center gap-1 text-ui-ink-3">
                            <MapPin className="w-4 h-4 flex-shrink-0" />
                            <span>
                              {meetLine}
                              {meetLine && sign ? ' · ' : ''}
                              {sign ? `Sign: ${sign}` : ''}
                            </span>
                          </div>
                        )}
                      </>
                    );
                  })()
                ) : component.component_type === 'memo' ? (
                  <>
                    {/* Line 3 for memo: Notes snippet */}
                    {(() => {
                      const cd = component.component_data || {};
                      const memoContent = cd.memo_content || component.description || '';
                      if (memoContent) {
                        const snippet = memoContent.length > 100 ? memoContent.substring(0, 100) + '...' : memoContent;
                        return (
                          <div className="w-full flex items-center gap-1 mb-2 text-ui-ink-3">
                            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="italic text-ui-ink-3">{snippet}</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {/* Line 4 for memo: Executives/Attendees */}
                    {(() => {
                      const cd = component.component_data || {};
                      const primaryExecId = cd.primary_executive_id;
                      const attendees = cd.memo_attendees || [];
                      const attendeeNames = attendees.map(a => a?.name).filter(Boolean);
                      const entries = [];

                      // Map primary executive ID to name using trip executives
                      if (primaryExecId) {
                        const tripExecs = trip?.executives || [];
                        const primaryExec = tripExecs.find(exec => exec.id === primaryExecId);
                        if (primaryExec) {
                          const primaryName = primaryExec.full_name || `${primaryExec.first_name} ${primaryExec.last_name}`;
                          entries.push({ name: primaryName, emphasis: true });
                        }
                      }

                      attendeeNames.forEach(name => entries.push({ name, emphasis: false }));

                      if (entries.length === 0) return null;

                      return (
                        <div className="mb-2">
                          <PassengerNameList names={entries} />
                        </div>
                      );
                    })()}
                  </>
                ) : component.component_type === 'event' ? (
                  (() => {
                    const cd = component.component_data || {};
                    const venueName = cd.event_venue_name || '';
                    const address = cd.event_location || component.location || '';
                    // Show venue name + address when both exist, otherwise just the location
                    const displayText = venueName && address ? `${venueName}, ${address}` : (venueName || address || component.location);
                    if (!displayText || displayText.trim() === '') return null;
                    return (
                      <div className="flex items-center gap-1 text-ui-ink-3">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{displayText}</span>
                      </div>
                    );
                  })()
                ) : (
                  component.location && (
                    <div className="flex items-center gap-1 text-ui-ink-3">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>{component.location && component.location.trim() !== '' ? component.location : 'TBD'}</span>
                    </div>
                  )
                )
              )}
            </div>
          </div>
        </div>

        {/* Actions - always visible on mobile, fade in on hover for desktop.
            Hidden entirely for view-only speaker accounts. */}
        {!readOnly && (
        <div className="flex items-center gap-1 sm:gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
          {/* Hide top-level edit for grouped multi-passenger flights - use per-passenger edit in dropdown instead */}
          {!(component.isGroupedFlightLeg && component.groupedComponents?.length > 1) && (
            <button
              onClick={() => {
                // For grouped flight legs, edit the first grouped component
                if (component.isGroupedFlightLeg && component.groupedComponents?.length > 0) {
                  onEdit(component.groupedComponents[0]);
                }
                // For flight legs (both commercial and private) and hotel events, edit the original component instead of the leg/event
                // Use the trip_component wrapper ID (component.id) not component_id
                else if ((component.isFlightLeg || component.isPrivateFlightLeg || component.isHotelEvent || component.isRentalCarEvent) && component.originalComponent) {
                  onEdit(component.originalComponent);
                } else {
                  onEdit(component);
                }
              }}
              className="p-1.5 sm:p-1 touch-target rounded-md text-ui-ink-3 hover:text-ui-ink hover:bg-ui-hover"
              title="Edit component"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}
          {/* Move to Trip button - hidden on mobile to save horizontal space */}
          {onReassign && allTrips.length > 1 && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowReassign(!showReassign);
              }}
              className="hidden sm:inline-flex p-1.5 sm:p-1 touch-target rounded-md items-center justify-center text-ui-ink-3 hover:text-ui-blue-fg hover:bg-ui-hover"
              title="Move to another trip"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
          {/* Report Parsing Issue button - hidden on mobile to save horizontal space */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowFeedbackModal(true);
            }}
            className="hidden sm:inline-flex p-1.5 sm:p-1 touch-target rounded-md items-center justify-center text-ui-ink-3 hover:text-ui-amber-fg hover:bg-ui-hover"
            title="Report parsing issue"
          >
            <AlertTriangle className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              // For grouped flight legs with multiple components, confirm before deleting all
              if (component.isGroupedFlightLeg && component.groupedComponents?.length > 1) {
                const passengerCount = component.groupedComponents.length;
                if (window.confirm(`This will delete all ${passengerCount} passenger bookings. To delete a single passenger's booking, use the dropdown in the details section. Continue?`)) {
                  // Delete all grouped components
                  component.groupedComponents.forEach(comp => onDelete(comp));
                }
                return;
              }

              // For flight legs (both commercial and private), hotel events, and rental car events, delete the original component
              // Pass the full component object (with id and tripId) to the delete handler
              if ((component.isFlightLeg || component.isPrivateFlightLeg || component.isHotelEvent || component.isRentalCarEvent) && component.originalComponent) {
                onDelete(component.originalComponent);
              } else {
                onDelete(component);
              }
            }}
            className="p-1.5 sm:p-1 z-10 relative touch-target rounded-md text-ui-ink-3 hover:text-ui-red-fg hover:bg-ui-hover"
            title={component.isGroupedFlightLeg && component.groupedComponents?.length > 1 ? "Delete all passenger bookings" : "Delete component"}
            style={{ pointerEvents: 'auto' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
        )}
      </div>

      {bodySections}

      {feedbackModal}
    </div>
  );
};

export default TimelineComponentCard;
